import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PasswordInput } from "@/components/PasswordInput";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Trash2, Plus, Pencil, Upload, FileSpreadsheet, AlertTriangle, ShieldAlert, Download, DatabaseBackup, RotateCcw, RefreshCw, CalendarDays, MapPin, Hammer, Send, Globe, Loader2, FileText, BookOpenCheck } from "lucide-react";
import { roleLabel } from "@/lib/permissions";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const APP_ROLES = [
  { code: "admin", label: "System Admin" },
  { code: "crew_chief", label: "User Admin" },
  { code: "engineer", label: "User" },
];

export default function AdminSettings() {
  const { user: me } = useAuth();
  const isSysAdmin = me?.role === "admin";
  const [shifts, setShifts] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [reviewRatings, setReviewRatings] = useState([]);
  const [templateSettings, setTemplateSettings] = useState(null);
  const [templateUploading, setTemplateUploading] = useState(false);
  const [rdbTemplate, setRdbTemplate] = useState(null);
  const [rdbUploading, setRdbUploading] = useState(false);
  const [templateLastResult, setTemplateLastResult] = useState(null);
  const [templateAircraftList, setTemplateAircraftList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newDept, setNewDept] = useState({ name: "" });

  const [newShift, setNewShift] = useState({ name: "", days_on: 0, days_off: 0, weekdays_only: false });
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "", locations: ["ALL"] });
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", body: "", variant: "info", active: true });
  const [newTaskType, setNewTaskType] = useState({ name: "" });
  const [newRating, setNewRating] = useState({ name: "" });

  const emptyUser = () => ({
    email: "", username: "", password: "demo123", role: "engineer",
    first_name: "", last_name: "", first_initial: "",
    employee_number: "", department: "", location: "AB",
    city: "",
    job_title: "Employee", max_daily_hours: 8, shift_schedule: "weekdays",
    first_day_of_shift: "Monday", true_up_applied: "No",
    employment_type: "full_time",
    per_diem_cdn: false, per_diem_int: false,
    manager_id: "",
    shift_range_start: "", shift_range_end: "", shift_days_off_override: "",
  });
  const [newUser, setNewUser] = useState(emptyUser());
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [cities, setCities] = useState([]);
  const [newCity, setNewCity] = useState({ code: "", name: "", latitude: "", longitude: "", radius_km: "1" });
  const [editCity, setEditCity] = useState(null);
  const [activeTab, setActiveTab] = useState("users");

  const loadAll = async () => {
    const [a, b, c, d, e, f, g, h, i, j, k, l, m] = await Promise.all([
      api.get("/shift-schedules"),
      api.get("/stat-holidays"),
      api.get("/locations"),
      api.get("/admin/users"),
      api.get("/job-titles"),
      api.get("/announcements"),
      api.get("/task-types"),
      api.get("/review-ratings"),
      api.get("/admin/timesheet-template"),
      api.get("/aircraft"),
      api.get("/admin/departments"),
      api.get("/admin/cities"),
      api.get("/admin/timesheet-template-rdb"),
    ]);
    setShifts(a.data);
    setHolidays(b.data);
    setLocations(c.data);
    setUsers(d.data);
    setJobTitles(e.data);
    setAnnouncements(f.data);
    setTaskTypes(g.data);
    setReviewRatings(h.data);
    setTemplateSettings(i.data);
    setTemplateAircraftList(j.data);
    setDepartments(k.data);
    setCities(l.data);
    setRdbTemplate(m.data);
  };

  useEffect(() => { loadAll(); }, []);

  // Shift schedules
  const addShift = async () => {
    if (!newShift.name.trim()) { toast.error("Shift name required"); return; }
    try {
      await api.post("/shift-schedules", newShift);
      toast.success("Shift schedule added");
      setNewShift({ name: "", days_on: 0, days_off: 0, weekdays_only: false });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeShift = async (id) => {
    try { await api.delete(`/shift-schedules/${id}`); toast.success("Removed"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Stat holidays
  const addHoliday = async () => {
    if (!newHoliday.date || !newHoliday.name) { toast.error("Date and name required"); return; }
    try {
      await api.post("/stat-holidays", newHoliday);
      toast.success("Holiday added");
      setNewHoliday({ date: "", name: "", locations: ["ALL"] });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeHoliday = async (id) => {
    try { await api.delete(`/stat-holidays/${id}`); toast.success("Removed"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleHolidayLocation = (code) => {
    setNewHoliday((h) => {
      if (code === "ALL") return { ...h, locations: ["ALL"] };
      const cur = h.locations.filter((c) => c !== "ALL");
      const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
      return { ...h, locations: next.length ? next : ["ALL"] };
    });
  };

  // Users
  const submitNewUser = async () => {
    // Front-end guard: make sure the admin really did fill every required
    // field before we round-trip the API. Server re-validates but we prefer
    // failing fast with a clear error instead of a generic 400.
    const required = [
      ["email", "Email"],
      ["password", "Password"],
      ["first_name", "First name"],
      ["last_name", "Last name"],
      ["employee_number", "Employee number"],
      ["department", "Department"],
      ["location", "Location"],
      ["city", "City (base)"],
      ["job_title", "Job title"],
      ["shift_schedule", "Shift schedule"],
      ["first_day_of_shift", "First day of shift"],
      ["true_up_applied", "True-up applied"],
    ];
    for (const [key, label] of required) {
      if (!newUser[key] || String(newUser[key]).trim() === "") {
        toast.error(`${label} is required.`);
        return;
      }
    }
    if (!newUser.max_daily_hours || Number(newUser.max_daily_hours) <= 0) {
      toast.error("Max daily hours must be greater than 0.");
      return;
    }
    // Shift range: both dates set together, or both empty.
    const sStart = (newUser.shift_range_start || "").trim();
    const sEnd = (newUser.shift_range_end || "").trim();
    if ((sStart && !sEnd) || (sEnd && !sStart)) {
      toast.error("Shift range Start and End must be filled together, or both left blank.");
      return;
    }
    try {
      const body = {
        ...newUser,
        first_initial: newUser.first_initial || newUser.first_name.slice(0, 1).toUpperCase(),
        manager_id: newUser.manager_id || null,
        username: (newUser.username || "").trim().toLowerCase() || null,
        shift_range_start: sStart || null,
        shift_range_end: sEnd || null,
        shift_days_off_override:
          newUser.shift_days_off_override === "" || newUser.shift_days_off_override === null || newUser.shift_days_off_override === undefined
            ? null
            : Number(newUser.shift_days_off_override),
      };
      await api.post("/admin/users", body);
      toast.success("User created");
      setUserDialogOpen(false);
      setNewUser(emptyUser());
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const submitEditUser = async () => {
    try {
      const upd = { ...editUser };
      delete upd.id; delete upd.display_name; delete upd.can_announce;
      if (!upd.password) delete upd.password;
      // Coerce empty manager_id to null so admin can clear a manager assignment.
      if (upd.manager_id === "" || upd.manager_id === undefined) upd.manager_id = null;
      // Normalise the optional shift-range block — send blank strings for
      // "clear", pass dates through as-is; drop days-off override if empty.
      upd.shift_range_start = (upd.shift_range_start || "").trim() || "";
      upd.shift_range_end = (upd.shift_range_end || "").trim() || "";
      if (upd.shift_days_off_override === "" || upd.shift_days_off_override === undefined) {
        delete upd.shift_days_off_override;
      }
      await api.patch(`/admin/users/${editUser.id}`, upd);
      toast.success("User updated");
      setEditUser(null);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.display_name}? This removes all their sessions.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deleted"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Announcements
  const addAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.body.trim()) { toast.error("Title and body required"); return; }
    try {
      await api.post("/announcements", newAnnouncement);
      toast.success("Announcement posted");
      setNewAnnouncement({ title: "", body: "", variant: "info", active: true });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeAnnouncement = async (id) => {
    try { await api.delete(`/announcements/${id}`); toast.success("Removed"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Task types
  const addTaskType = async () => {
    if (!newTaskType.name.trim()) { toast.error("Task name required"); return; }
    try {
      await api.post("/task-types", newTaskType);
      toast.success("Task type added");
      setNewTaskType({ name: "" });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeTaskType = async (id) => {
    if (!window.confirm("Remove this task type?")) return;
    try { await api.delete(`/task-types/${id}`); toast.success("Removed"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Review ratings
  const addRating = async () => {
    if (!newRating.name.trim()) { toast.error("Rating name required"); return; }
    try {
      await api.post("/review-ratings", newRating);
      toast.success("Rating added");
      setNewRating({ name: "" });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeRating = async (id) => {
    if (!window.confirm("Remove this rating category? Reviews already using it keep the label.")) return;
    try { await api.delete(`/review-ratings/${id}`); toast.success("Removed"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Departments — admin CRUD; validation on user create/edit + profile page.
  const addDept = async () => {
    if (!newDept.name.trim()) { toast.error("Department name required"); return; }
    try {
      await api.post("/admin/departments", { name: newDept.name.trim() });
      toast.success("Department added");
      setNewDept({ name: "" });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const renameDept = async (id, currentName) => {
    const next = window.prompt(`Rename department:`, currentName);
    if (next === null) return;
    const nextName = next.trim();
    if (!nextName || nextName === currentName) return;
    try {
      await api.patch(`/admin/departments/${id}`, { name: nextName });
      toast.success("Renamed");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleDept = async (id, active) => {
    try {
      await api.patch(`/admin/departments/${id}`, { active: !active });
      toast.success(active ? "Deactivated" : "Reactivated");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeDept = async (id, name) => {
    if (!window.confirm(`Remove department "${name}"? Users assigned to it must be reassigned first.`)) return;
    try {
      await api.delete(`/admin/departments/${id}`);
      toast.success("Removed");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };
  const setDeptL2 = async (id, uid) => {
    try {
      await api.patch(`/admin/departments/${id}`, { l2_approver_id: uid });
      toast.success(uid ? "L2 approver set" : "L2 approver cleared — manager chain applies");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const activeDepartments = React.useMemo(() => departments.filter((d) => d.active), [departments]);

  // Cities — admin CRUD with geofence lat/lng/radius.
  const addCity = async () => {
    const code = (newCity.code || "").trim().toUpperCase();
    const name = (newCity.name || "").trim();
    if (!code || !name) { toast.error("City code and name required"); return; }
    const body = { code, name };
    if (newCity.latitude !== "" && newCity.longitude !== "") {
      const lat = parseFloat(newCity.latitude);
      const lng = parseFloat(newCity.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) { toast.error("Latitude / longitude must be numbers"); return; }
      body.latitude = lat;
      body.longitude = lng;
    } else if (newCity.latitude !== "" || newCity.longitude !== "") {
      toast.error("Set both latitude and longitude, or leave both blank."); return;
    }
    if (newCity.radius_km !== "") {
      const r = parseFloat(newCity.radius_km);
      if (Number.isNaN(r) || r <= 0) { toast.error("Radius must be a positive number"); return; }
      body.radius_km = r;
    }
    try {
      await api.post("/admin/cities", body);
      toast.success(`City ${code} added`);
      setNewCity({ code: "", name: "", latitude: "", longitude: "", radius_km: "1" });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add city"); }
  };
  const saveEditCity = async () => {
    if (!editCity) return;
    const body = { name: (editCity.name || "").trim(), code: (editCity.code || "").trim().toUpperCase() };
    const latStr = editCity.latitude ?? "";
    const lngStr = editCity.longitude ?? "";
    if ((latStr === "" || latStr === null) !== (lngStr === "" || lngStr === null)) {
      toast.error("Set both latitude and longitude, or clear both together."); return;
    }
    body.latitude = (latStr === "" || latStr === null) ? null : parseFloat(latStr);
    body.longitude = (lngStr === "" || lngStr === null) ? null : parseFloat(lngStr);
    if (body.latitude !== null && Number.isNaN(body.latitude)) { toast.error("Latitude must be a number"); return; }
    if (body.longitude !== null && Number.isNaN(body.longitude)) { toast.error("Longitude must be a number"); return; }
    if (editCity.radius_km !== "" && editCity.radius_km !== null) {
      const r = parseFloat(editCity.radius_km);
      if (Number.isNaN(r) || r <= 0) { toast.error("Radius must be positive"); return; }
      body.radius_km = r;
    }
    try {
      await api.patch(`/admin/cities/${editCity.id}`, body);
      toast.success("City updated");
      setEditCity(null);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save city"); }
  };
  const toggleCity = async (city) => {
    try {
      await api.patch(`/admin/cities/${city.id}`, { active: !city.active });
      toast.success(city.active ? "Deactivated" : "Reactivated");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const [addrCity, setAddrCity] = useState(null);
  const [addrText, setAddrText] = useState("");
  const saveCityAddress = async () => {
    if (!addrCity) return;
    try {
      await api.patch(`/admin/cities/${addrCity.id}`, { address: addrText.trim() });
      toast.success(`Address saved for ${addrCity.name}`);
      setAddrCity(null);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save address"); }
  };
  const removeCity = async (city) => {
    if (!window.confirm(`Remove ${city.code} (${city.name})?`)) return;
    try {
      await api.delete(`/admin/cities/${city.id}`);
      toast.success("City removed");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove city"); }
  };

  // Timesheet template upload
  const uploadTemplate = async (file) => {
    if (!file) return;
    if (!/\.(xlsm|xlsx)$/i.test(file.name)) { toast.error("File must be .xlsm or .xlsx"); return; }
    setTemplateUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/timesheet-template", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTemplateLastResult(data.aircraft_changes);
      toast.success(
        `Template uploaded · ${data.aircraft_changes.added.length} added, ${data.aircraft_changes.removed.length} removed`,
      );
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setTemplateUploading(false);
    }
  };

  // RDB Merge export template
  const uploadRdbTemplate = async (file) => {
    if (!file) return;
    if (!/\.(xlsm|xlsx)$/i.test(file.name)) { toast.error("File must be .xlsm or .xlsx"); return; }
    setRdbUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/timesheet-template-rdb", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRdbTemplate(data);
      toast.success("RDB Merge export template set — exports now keep formulas intact");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setRdbUploading(false);
    }
  };

  const removeRdbTemplate = async () => {
    if (!window.confirm("Remove the RDB Merge export template? Exports will go back to the standard format.")) return;
    try {
      await api.delete("/admin/timesheet-template-rdb");
      setRdbTemplate({ filename: null });
      toast.success("RDB Merge export template removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-settings">
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Admin Settings · MST</div>
      <h1 className="font-display text-4xl font-semibold tracking-tight mt-1">Configuration</h1>
      <p className="text-sm text-gray-500 mt-1">Manage users, announcements, schedules and holidays.</p>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8" data-testid="admin-tabs-root">
        {/* Two-tier grouping (iter-46 redesign): group chips select a
            section, then the section's tab strip appears below. Each
            section chip carries an emoji + label. Every original tab
            value + data-testid is preserved so tests keep working. */}
        <AdminGroupedTabsList activeTab={activeTab} onActivate={setActiveTab} hideSystem={!isSysAdmin} />

        {/* USERS TAB */}
        <TabsContent value="users" className="mt-6">
          <div className="border border-summit-hairline rounded-lg bg-white">
            <div className="px-5 py-4 border-b border-summit-hairline flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-display text-lg font-medium tracking-tight">Users</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  className="h-9 w-56"
                  placeholder="Search name, username, emp #…"
                  value={userQuery}
                  onChange={(e) => setUserQuery(e.target.value)}
                  data-testid="users-search"
                />
                <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" data-testid="add-user-open"><Plus className="w-4 h-4 mr-1"/> Add user</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader><DialogTitle>Add user</DialogTitle></DialogHeader>
                  <div className="grid md:grid-cols-2 gap-3 mt-2">
                    <div><Label className="text-xs uppercase tracking-widest text-gray-500">Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} data-testid="new-user-email" /></div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">Username <span className="text-summit-primary-soft/60">(auto if empty)</span></Label>
                      <Input
                        value={newUser.username}
                        onChange={(e) => setNewUser({...newUser, username: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "")})}
                        placeholder={
                          newUser.first_initial || newUser.first_name
                            ? `${(newUser.first_initial || newUser.first_name[0] || "").toLowerCase()}${(newUser.last_name || "").toLowerCase().replace(/[^a-z0-9]/g, "")}`
                            : "e.g. jperpetua"
                        }
                        data-testid="new-user-username"
                      />
                    </div>
                    <div><Label className="text-xs uppercase tracking-widest text-gray-500">Password</Label><PasswordInput autoComplete="new-password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} data-testid="new-user-password" /></div>
                    <div><Label className="text-xs uppercase tracking-widest text-gray-500">First name</Label><Input value={newUser.first_name} onChange={(e) => setNewUser({...newUser, first_name: e.target.value})} data-testid="new-user-first-name" /></div>
                    <div><Label className="text-xs uppercase tracking-widest text-gray-500">Last name</Label><Input value={newUser.last_name} onChange={(e) => setNewUser({...newUser, last_name: e.target.value})} data-testid="new-user-last-name" /></div>
                    <div><Label className="text-xs uppercase tracking-widest text-gray-500">Employee #</Label><Input value={newUser.employee_number} onChange={(e) => setNewUser({...newUser, employee_number: e.target.value})} data-testid="new-user-emp" /></div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">Department</Label>
                      <Select
                        value={newUser.department || ""}
                        onValueChange={(v) => setNewUser({ ...newUser, department: v })}
                      >
                        <SelectTrigger data-testid="new-user-dept"><SelectValue placeholder="Choose…" /></SelectTrigger>
                        <SelectContent>
                          {activeDepartments.map((d) => (
                            <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">App role</Label>
                      <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                        <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {APP_ROLES.filter((r) => isSysAdmin || r.code !== "admin").map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">Job title</Label>
                      <Select value={newUser.job_title} onValueChange={(v) => setNewUser({...newUser, job_title: v})}>
                        <SelectTrigger data-testid="new-user-job-title"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {jobTitles.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">Location · required</Label>
                      <Select value={newUser.location} onValueChange={(v) => setNewUser({...newUser, location: v})}>
                        <SelectTrigger data-testid="new-user-location"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {locations.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">City (base) · required</Label>
                      <Select value={newUser.city || ""} onValueChange={(v) => setNewUser({...newUser, city: v})}>
                        <SelectTrigger data-testid="new-user-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                        <SelectContent>
                          {cities.map((c) => (
                            <SelectItem key={c.code} value={c.code} data-testid={`new-user-city-option-${c.code}`}>
                              {c.code} · {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">Shift · required</Label>
                      <Select value={newUser.shift_schedule} onValueChange={(v) => setNewUser({...newUser, shift_schedule: v})}>
                        <SelectTrigger data-testid="new-user-shift"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">Employment · required</Label>
                      <Select value={newUser.employment_type || "full_time"} onValueChange={(v) => setNewUser({...newUser, employment_type: v})}>
                        <SelectTrigger data-testid="new-user-employment"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time" data-testid="new-user-employment-full">Full time</SelectItem>
                          <SelectItem value="part_time" data-testid="new-user-employment-part">Part time</SelectItem>
                        </SelectContent>
                      </Select>
                      {newUser.employment_type === "part_time" && (
                        <p className="text-[11px] text-gray-500 mt-1" data-testid="new-user-part-time-hint">No fixed schedule — timesheet won't project days on/off.</p>
                      )}
                    </div>
                    <div><Label className="text-xs uppercase tracking-widest text-gray-500">Max daily hrs · required</Label><Input type="number" step="0.1" value={newUser.max_daily_hours} onChange={(e) => setNewUser({...newUser, max_daily_hours: parseFloat(e.target.value)||0})} data-testid="new-user-max-hours" /></div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">First day of shift · required</Label>
                      <Select value={newUser.first_day_of_shift} onValueChange={(v) => setNewUser({...newUser, first_day_of_shift: v})}>
                        <SelectTrigger data-testid="new-user-first-day"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500">True-up applied · required</Label>
                      <Select value={newUser.true_up_applied} onValueChange={(v) => setNewUser({...newUser, true_up_applied: v})}>
                        <SelectTrigger data-testid="new-user-true-up"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-widest text-gray-500">Reports to (manager)</Label>
                      <Select
                        value={newUser.manager_id || "__none__"}
                        onValueChange={(v) => setNewUser({...newUser, manager_id: v === "__none__" ? "" : v})}
                      >
                        <SelectTrigger data-testid="new-user-manager"><SelectValue placeholder="No manager" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No manager (unassigned)</SelectItem>
                          {users.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.display_name} · {m.job_title || roleLabel(m.role)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2 border-t border-summit-hairline pt-3 mt-1">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Per-diem eligibility</div>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center gap-3 text-sm cursor-pointer">
                          <Switch checked={!!newUser.per_diem_cdn} onCheckedChange={(v) => setNewUser({...newUser, per_diem_cdn: v})} data-testid="new-user-per-diem-cdn" />
                          <span>Eligible for Canadian per-diem</span>
                        </label>
                        <label className="flex items-center gap-3 text-sm cursor-pointer">
                          <Switch checked={!!newUser.per_diem_int} onCheckedChange={(v) => setNewUser({...newUser, per_diem_int: v})} data-testid="new-user-per-diem-int" />
                          <span>Eligible for International per-diem</span>
                        </label>
                      </div>
                    </div>
                    <div className="md:col-span-2 border-t border-summit-hairline pt-3 mt-1">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Shift range · optional</div>
                      <p className="text-xs text-gray-500 mb-2">Set a hard bracket for auto-projected days-off, or leave blank to use the shift-schedule default.</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-gray-500">Start</Label>
                          <Input type="date" value={newUser.shift_range_start || ""} onChange={(e) => setNewUser({...newUser, shift_range_start: e.target.value})} data-testid="new-user-shift-range-start" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-gray-500">End</Label>
                          <Input type="date" value={newUser.shift_range_end || ""} onChange={(e) => setNewUser({...newUser, shift_range_end: e.target.value})} data-testid="new-user-shift-range-end" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-gray-500">Days off override</Label>
                          <Input type="number" min="0" step="1" value={newUser.shift_days_off_override ?? ""} onChange={(e) => setNewUser({...newUser, shift_days_off_override: e.target.value})} data-testid="new-user-shift-days-off-override" placeholder="Auto" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Cancel</Button>
                    <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitNewUser} data-testid="save-new-user">Create user</Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-summit-hairline">
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Emp #</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Username</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Email</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Role</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Job title</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Location</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter((u) => {
                    const q = userQuery.trim().toLowerCase();
                    if (!q) return true;
                    return [u.display_name, u.first_name, u.last_name, u.username, u.email, u.employee_number, u.job_title, u.location, u.city]
                      .filter(Boolean).join(" ").toLowerCase().includes(q);
                  }).map((u) => (
                    <tr key={u.id} className="border-b border-summit-hairline last:border-0" data-testid={`user-row-${u.employee_number}`}>
                      <td className="px-4 py-2">
                        {u.display_name}
                        {u.employment_type === "part_time" && (
                          <span className="ml-1.5 font-mono text-[9px] uppercase tracking-widest border border-violet-200 bg-violet-50 text-violet-700 rounded px-1 py-0.5 align-middle" title="Part time — no fixed schedule" data-testid={`admin-pt-badge-${u.id}`}>PT</span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono">{u.employee_number}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-summit-primary">{u.username || "—"}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{u.email}</td>
                      <td className="px-4 py-2"><Badge variant="secondary" className="font-mono text-[10px]" data-testid={`user-role-badge-${u.employee_number}`}>{roleLabel(u.role)}</Badge></td>
                      <td className="px-4 py-2 text-xs">{u.job_title}</td>
                      <td className="px-4 py-2 font-mono">{u.location || "—"}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => setEditUser({ ...u, password: "" })} className="p-1.5 rounded-md hover:bg-summit-glacier text-gray-600 mr-1 disabled:opacity-30 disabled:pointer-events-none" disabled={!isSysAdmin && u.role === "admin"} title={!isSysAdmin && u.role === "admin" ? "System Admin accounts can only be changed by a System Admin" : ""} data-testid={`edit-user-${u.employee_number}`}><Pencil className="w-4 h-4"/></button>
                        <button onClick={() => deleteUser(u)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600 disabled:opacity-30" disabled={(u.role === "admin" && users.filter((x) => x.role === "admin").length <= 1) || (!isSysAdmin && u.role === "admin")} title={!isSysAdmin && u.role === "admin" ? "System Admin accounts can only be changed by a System Admin" : (u.role === "admin" && users.filter((x) => x.role === "admin").length <= 1 ? "Last System Admin — protected" : "")} data-testid={`delete-user-${u.employee_number}`}><Trash2 className="w-4 h-4"/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Edit user{editUser ? ` · ${editUser.display_name}` : ""}</DialogTitle></DialogHeader>
              {editUser && (
                <div className="grid md:grid-cols-2 gap-3 mt-2">
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500">First name</Label><Input value={editUser.first_name || ""} onChange={(e) => setEditUser({...editUser, first_name: e.target.value})} data-testid="edit-user-first-name" /></div>
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500">Last name</Label><Input value={editUser.last_name || ""} onChange={(e) => setEditUser({...editUser, last_name: e.target.value})} data-testid="edit-user-last-name" /></div>
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500">First initial</Label><Input maxLength={1} value={editUser.first_initial || ""} onChange={(e) => setEditUser({...editUser, first_initial: e.target.value.toUpperCase().slice(0,1)})} data-testid="edit-user-first-initial" /></div>
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500">Employee #</Label><Input value={editUser.employee_number || ""} onChange={(e) => setEditUser({...editUser, employee_number: e.target.value})} data-testid="edit-user-emp" /></div>
                  <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest text-gray-500">Email</Label><Input type="email" value={editUser.email || ""} onChange={(e) => setEditUser({...editUser, email: e.target.value})} data-testid="edit-user-email" /></div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Department</Label>
                    <Select
                      value={editUser.department || ""}
                      onValueChange={(v) => setEditUser({ ...editUser, department: v })}
                    >
                      <SelectTrigger data-testid="edit-user-dept"><SelectValue placeholder="Choose…" /></SelectTrigger>
                      <SelectContent>
                        {activeDepartments.map((d) => (
                          <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">App role</Label>
                    <Select value={editUser.role} onValueChange={(v) => setEditUser({...editUser, role: v})}>
                      <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {APP_ROLES.filter((r) => isSysAdmin || r.code !== "admin").map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Job title</Label>
                    <Select value={editUser.job_title} onValueChange={(v) => setEditUser({...editUser, job_title: v})}>
                      <SelectTrigger data-testid="edit-user-job-title"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {jobTitles.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Location</Label>
                    <Select value={editUser.location} onValueChange={(v) => setEditUser({...editUser, location: v})}>
                      <SelectTrigger data-testid="edit-user-location"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {locations.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">City (base)</Label>
                    <Select value={editUser.city || ""} onValueChange={(v) => setEditUser({...editUser, city: v})}>
                      <SelectTrigger data-testid="edit-user-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent>
                        {cities.map((c) => (
                          <SelectItem key={c.code} value={c.code} data-testid={`edit-user-city-option-${c.code}`}>
                            {c.code} · {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Shift</Label>
                    <Select value={editUser.shift_schedule} onValueChange={(v) => setEditUser({...editUser, shift_schedule: v})}>
                      <SelectTrigger data-testid="edit-user-shift"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Employment</Label>
                    <Select value={editUser.employment_type || "full_time"} onValueChange={(v) => setEditUser({...editUser, employment_type: v})}>
                      <SelectTrigger data-testid="edit-user-employment"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time" data-testid="edit-user-employment-full">Full time</SelectItem>
                        <SelectItem value="part_time" data-testid="edit-user-employment-part">Part time</SelectItem>
                      </SelectContent>
                    </Select>
                    {editUser.employment_type === "part_time" && (
                      <p className="text-[11px] text-gray-500 mt-1" data-testid="edit-user-part-time-hint">No fixed schedule — timesheet won't project days on/off.</p>
                    )}
                  </div>
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500">Max daily hrs</Label><Input type="number" step="0.1" value={editUser.max_daily_hours ?? 0} onChange={(e) => setEditUser({...editUser, max_daily_hours: parseFloat(e.target.value)||0})} data-testid="edit-user-max-hours" /></div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">First day of shift</Label>
                    <Select value={editUser.first_day_of_shift || ""} onValueChange={(v) => setEditUser({...editUser, first_day_of_shift: v})}>
                      <SelectTrigger data-testid="edit-user-first-day"><SelectValue placeholder="Choose…" /></SelectTrigger>
                      <SelectContent>
                        {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">True-up applied</Label>
                    <Select value={editUser.true_up_applied || "No"} onValueChange={(v) => setEditUser({...editUser, true_up_applied: v})}>
                      <SelectTrigger data-testid="edit-user-true-up"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Reports to (manager)</Label>
                    <Select
                      value={editUser.manager_id || "__none__"}
                      onValueChange={(v) => setEditUser({...editUser, manager_id: v === "__none__" ? "" : v})}
                    >
                      <SelectTrigger data-testid="edit-user-manager"><SelectValue placeholder="No manager" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No manager (unassigned)</SelectItem>
                        {users.filter((m) => m.id !== editUser.id).map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.display_name} · {m.job_title || roleLabel(m.role)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Username</Label>
                    <Input
                      value={editUser.username || ""}
                      onChange={(e) => setEditUser({...editUser, username: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "")})}
                      data-testid="edit-user-username"
                      placeholder="e.g. jperpetua"
                    />
                  </div>
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500">Reset password (leave blank to keep)</Label><PasswordInput autoComplete="new-password" value={editUser.password || ""} onChange={(e) => setEditUser({...editUser, password: e.target.value})} data-testid="edit-user-password" /></div>
                  <div className="md:col-span-2 border-t border-summit-hairline pt-3 mt-1">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Per-diem eligibility</div>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center gap-3 text-sm cursor-pointer">
                        <Switch checked={!!editUser.per_diem_cdn} onCheckedChange={(v) => setEditUser({...editUser, per_diem_cdn: v})} data-testid="edit-user-per-diem-cdn" />
                        <span>Eligible for Canadian per-diem</span>
                      </label>
                      <label className="flex items-center gap-3 text-sm cursor-pointer">
                        <Switch checked={!!editUser.per_diem_int} onCheckedChange={(v) => setEditUser({...editUser, per_diem_int: v})} data-testid="edit-user-per-diem-int" />
                        <span>Eligible for International per-diem</span>
                      </label>
                    </div>
                  </div>
                  <div className="md:col-span-2 border-t border-summit-hairline pt-3 mt-1">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Shift range · optional</div>
                    <p className="text-xs text-gray-500 mb-2">Hard bracket for auto-projected days-off. Clear both dates to fall back to the shift-schedule default.</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-500">Start</Label>
                        <Input type="date" value={editUser.shift_range_start || ""} onChange={(e) => setEditUser({...editUser, shift_range_start: e.target.value})} data-testid="edit-user-shift-range-start" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-500">End</Label>
                        <Input type="date" value={editUser.shift_range_end || ""} onChange={(e) => setEditUser({...editUser, shift_range_end: e.target.value})} data-testid="edit-user-shift-range-end" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-500">Days off override</Label>
                        <Input type="number" min="0" step="1" value={editUser.shift_days_off_override ?? ""} onChange={(e) => setEditUser({...editUser, shift_days_off_override: e.target.value === "" ? null : parseInt(e.target.value, 10)})} data-testid="edit-user-shift-days-off-override" placeholder="Auto" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
                <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitEditUser} data-testid="save-edit-user">Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ANNOUNCEMENTS TAB */}
        <TabsContent value="announcements" className="mt-6">
          <div className="border border-summit-hairline rounded-lg bg-white p-5">
            <h3 className="font-display text-lg font-medium tracking-tight">Post announcement</h3>
            <p className="text-sm text-gray-500 mt-1">Shown to all logged-in users as a banner.</p>
            <div className="mt-4 space-y-3">
              <div><Label className="text-xs uppercase tracking-widest text-gray-500">Title</Label><Input value={newAnnouncement.title} onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})} data-testid="new-announcement-title" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-gray-500">Body</Label><Input value={newAnnouncement.body} onChange={(e) => setNewAnnouncement({...newAnnouncement, body: e.target.value})} data-testid="new-announcement-body" /></div>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Variant</Label>
                  <Select value={newAnnouncement.variant} onValueChange={(v) => setNewAnnouncement({...newAnnouncement, variant: v})}>
                    <SelectTrigger data-testid="new-announcement-variant"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="success">Success</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={addAnnouncement} data-testid="add-announcement-btn">
                <Plus className="w-4 h-4 mr-1"/> Post
              </Button>
            </div>
          </div>

          <div className="mt-6 border border-summit-hairline rounded-lg bg-white">
            <div className="px-5 py-4 border-b border-summit-hairline">
              <h2 className="font-display text-lg font-medium tracking-tight">Recent announcements</h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-summit-hairline">
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Title</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Variant</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Author</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Active</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {announcements.length === 0 && (<tr><td colSpan="5" className="px-4 py-6 text-center text-gray-400 text-sm">No announcements yet.</td></tr>)}
                {announcements.map((a) => (
                  <tr key={a.id} className="border-b border-summit-hairline last:border-0" data-testid={`announcement-row-${a.id}`}>
                    <td className="px-4 py-2 font-medium">{a.title}</td>
                    <td className="px-4 py-2"><Badge variant="secondary" className="font-mono text-[10px]">{a.variant}</Badge></td>
                    <td className="px-4 py-2 text-xs">{a.author_name} · {a.author_job_title}</td>
                    <td className="px-4 py-2">{a.active ? "Yes" : "No"}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => removeAnnouncement(a.id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600" data-testid={`delete-announcement-${a.id}`}><Trash2 className="w-4 h-4"/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* SHIFTS TAB */}
        <TabsContent value="shifts" className="mt-6">
          <div className="border border-summit-hairline rounded-lg bg-white">
            <div className="px-5 py-4 border-b border-summit-hairline">
              <h2 className="font-display text-lg font-medium tracking-tight">Shift schedules</h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-summit-hairline">
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Days on</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Days off</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Weekdays only</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {shifts.map((s) => (
                  <tr key={s.id} className="border-b border-summit-hairline last:border-0" data-testid={`shift-row-${s.id}`}>
                    <td className="px-4 py-2 font-medium">{s.name}</td>
                    <td className="px-4 py-2 font-mono">{s.days_on}</td>
                    <td className="px-4 py-2 font-mono">{s.days_off}</td>
                    <td className="px-4 py-2">{s.weekdays_only ? "Yes" : "No"}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => removeShift(s.id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600 disabled:opacity-30" disabled={["weekdays","7-on-off","14-day"].includes(s.id)} data-testid={`delete-shift-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-5">
            <h3 className="font-display text-lg font-medium tracking-tight">Add schedule</h3>
            <div className="grid md:grid-cols-4 gap-3 mt-4">
              <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label><Input placeholder="e.g. 4 on / 4 off" value={newShift.name} onChange={(e) => setNewShift({...newShift, name: e.target.value})} data-testid="new-shift-name" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-gray-500">Days on</Label><Input type="number" min="0" value={newShift.days_on} onChange={(e) => setNewShift({...newShift, days_on: parseInt(e.target.value)||0})} data-testid="new-shift-days-on" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-gray-500">Days off</Label><Input type="number" min="0" value={newShift.days_off} onChange={(e) => setNewShift({...newShift, days_off: parseInt(e.target.value)||0})} data-testid="new-shift-days-off" /></div>
            </div>
            <div className="mt-4 flex justify-end"><Button onClick={addShift} className="bg-summit-primary hover:bg-summit-primary-soft text-white" data-testid="add-shift-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button></div>
          </div>
        </TabsContent>

        {/* TASK TYPES TAB */}
        <TabsContent value="tasks" className="mt-6">
          <div className="border border-summit-hairline rounded-lg bg-white">
            <div className="px-5 py-4 border-b border-summit-hairline">
              <h2 className="font-display text-lg font-medium tracking-tight">Aircraft task types</h2>
              <p className="text-xs text-gray-500 mt-1">Options shown when an engineer clocks in on an aircraft (Regular / OT / Acting CC).</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-summit-hairline">
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">ID</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {taskTypes.length === 0 && (
                  <tr><td colSpan="3" className="px-4 py-6 text-center text-gray-400 text-sm">No task types yet.</td></tr>
                )}
                {taskTypes.map((t) => (
                  <tr key={t.id} className="border-b border-summit-hairline last:border-0" data-testid={`task-type-row-${t.id}`}>
                    <td className="px-4 py-2 font-medium">{t.name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{t.id}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => removeTaskType(t.id)}
                        className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"
                        data-testid={`delete-task-type-${t.id}`}
                      ><Trash2 className="w-4 h-4"/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-5">
            <h3 className="font-display text-lg font-medium tracking-tight">Add task type</h3>
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label>
                <Input
                  placeholder="e.g. Engine Wash"
                  value={newTaskType.name}
                  onChange={(e) => setNewTaskType({ name: e.target.value })}
                  data-testid="new-task-type-name"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={addTaskType} className="bg-summit-primary hover:bg-summit-primary-soft text-white w-full" data-testid="add-task-type-btn">
                  <Plus className="w-4 h-4 mr-1"/> Add
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* DEPARTMENTS TAB — admin-editable list; also used for L1 approver widening */}
        <TabsContent value="departments" className="mt-6">
          <div className="border border-summit-hairline rounded-lg bg-white" data-testid="departments-panel">
            <div className="px-5 py-4 border-b border-summit-hairline">
              <h2 className="font-display text-lg font-medium tracking-tight">Departments</h2>
              <p className="text-xs text-gray-500 mt-1 max-w-2xl">
                Departments appear in the user profile + admin user editor dropdowns. Any Supervisor
                or Manager in a department can approve Level-1 timesheets for anyone else in the
                same department, additive to the user&apos;s direct manager. Setting an <b>L2 approver</b>{" "}
                makes that person the ONLY level-2 (Manager Sign Off) approver for everyone in the
                department — leave it unset to use the automatic manager-of-manager chain.
              </p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-summit-hairline">
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">ID</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">L2 approver</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {departments.length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-400 text-sm">No departments yet.</td></tr>
                )}
                {departments.map((d) => (
                  <tr key={d.id} className="border-b border-summit-hairline last:border-0" data-testid={`department-row-${d.id}`}>
                    <td className="px-4 py-2 font-medium">{d.name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{d.id}</td>
                    <td className="px-4 py-2">
                      <select
                        className="h-8 w-full max-w-[220px] px-2 rounded-md border border-summit-hairline bg-white text-xs"
                        value={d.l2_approver_id || ""}
                        onChange={(e) => setDeptL2(d.id, e.target.value)}
                        data-testid={`dept-l2-select-${d.id}`}
                      >
                        <option value="">— Manager chain —</option>
                        {users.map((m) => (
                          <option key={m.id} value={m.id}>{m.display_name} · {m.job_title || m.role}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      {d.active ? (
                        <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 font-mono text-[10px] uppercase tracking-widest" data-testid={`dept-status-${d.id}`}>Active</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-500 font-mono text-[10px] uppercase tracking-widest" data-testid={`dept-status-${d.id}`}>Inactive</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right space-x-1">
                      <button
                        onClick={() => renameDept(d.id, d.name)}
                        className="p-1.5 rounded-md hover:bg-summit-glacier text-summit-primary"
                        title="Rename"
                        data-testid={`rename-department-${d.id}`}
                      ><Pencil className="w-4 h-4"/></button>
                      <button
                        onClick={() => toggleDept(d.id, d.active)}
                        className="px-2 py-1 rounded-md font-mono text-[10px] uppercase tracking-widest hover:bg-summit-glacier text-summit-primary"
                        title={d.active ? "Deactivate" : "Reactivate"}
                        data-testid={`toggle-department-${d.id}`}
                      >{d.active ? "Deactivate" : "Reactivate"}</button>
                      <button
                        onClick={() => removeDept(d.id, d.name)}
                        className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"
                        title="Delete"
                        data-testid={`delete-department-${d.id}`}
                      ><Trash2 className="w-4 h-4"/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-5">
            <h3 className="font-display text-lg font-medium tracking-tight">Add department</h3>
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label>
                <Input
                  placeholder="e.g. Line Maintenance"
                  value={newDept.name}
                  onChange={(e) => setNewDept({ name: e.target.value })}
                  data-testid="new-department-name"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={addDept}
                  className="bg-summit-primary hover:bg-summit-primary-soft text-white w-full"
                  data-testid="add-department-btn"
                >
                  <Plus className="w-4 h-4 mr-1"/> Add
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* CITIES TAB — geofence per city with lat/lng + radius */}
        <TabsContent value="cities" className="mt-6">
          <div className="border border-summit-hairline rounded-lg bg-white" data-testid="cities-panel">
            <div className="px-5 py-4 border-b border-summit-hairline">
              <h2 className="font-display text-lg font-medium tracking-tight">Cities (base + geofence)</h2>
              <p className="text-xs text-gray-500 mt-1 max-w-3xl">
                Users must pick a city on their profile. If a city has a latitude + longitude set, clock-in
                will check the user&apos;s device GPS against the city center and flag any session started
                outside the configured radius (default <b>1 km</b>) for the admin audit trail. Cities with
                no coordinates skip the geofence check entirely.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-summit-hairline">
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Code</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Latitude</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Longitude</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Radius (km)</th>
                    <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Status</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {cities.length === 0 && (
                    <tr><td colSpan="7" className="px-4 py-6 text-center text-gray-400 text-sm">No cities yet.</td></tr>
                  )}
                  {cities.map((c) => (
                    <tr key={c.id} className="border-b border-summit-hairline last:border-0" data-testid={`city-row-${c.code}`}>
                      <td className="px-4 py-2 font-mono font-medium">{c.code}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-1.5">
                          {c.name}
                          <button
                            onClick={() => { setAddrCity(c); setAddrText(c.address || ""); }}
                            className="p-1 rounded-md hover:bg-summit-glacier text-summit-primary"
                            title={c.address ? `Base address: ${c.address}` : "Set base address"}
                            data-testid={`city-address-btn-${c.code}`}
                          ><MapPin className="w-3.5 h-3.5"/></button>
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono tabular-nums text-xs" data-testid={`city-lat-${c.code}`}>{c.latitude ?? "—"}</td>
                      <td className="px-4 py-2 font-mono tabular-nums text-xs" data-testid={`city-lng-${c.code}`}>{c.longitude ?? "—"}</td>
                      <td className="px-4 py-2 font-mono tabular-nums text-xs" data-testid={`city-radius-${c.code}`}>{c.radius_km ?? "1"}</td>
                      <td className="px-4 py-2">
                        {c.active ? (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 font-mono text-[10px] uppercase tracking-widest">Active</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-gray-100 text-gray-500 font-mono text-[10px] uppercase tracking-widest">Inactive</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right space-x-1 whitespace-nowrap">
                        <button
                          onClick={() => setEditCity({ ...c, latitude: c.latitude ?? "", longitude: c.longitude ?? "", radius_km: c.radius_km ?? 1 })}
                          className="p-1.5 rounded-md hover:bg-summit-glacier text-summit-primary"
                          title="Edit"
                          data-testid={`edit-city-${c.code}`}
                        ><Pencil className="w-4 h-4"/></button>
                        <button
                          onClick={() => toggleCity(c)}
                          className="px-2 py-1 rounded-md font-mono text-[10px] uppercase tracking-widest hover:bg-summit-glacier text-summit-primary"
                          title={c.active ? "Deactivate" : "Reactivate"}
                          data-testid={`toggle-city-${c.code}`}
                        >{c.active ? "Deactivate" : "Reactivate"}</button>
                        <button
                          onClick={() => removeCity(c)}
                          className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"
                          title="Delete"
                          data-testid={`delete-city-${c.code}`}
                        ><Trash2 className="w-4 h-4"/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-5">
            <h3 className="font-display text-lg font-medium tracking-tight">Add city</h3>
            <div className="grid md:grid-cols-6 gap-3 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Code</Label>
                <Input placeholder="YZF" value={newCity.code} onChange={(e) => setNewCity({ ...newCity, code: e.target.value.toUpperCase() })} data-testid="new-city-code" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label>
                <Input placeholder="Yellowknife, NT" value={newCity.name} onChange={(e) => setNewCity({ ...newCity, name: e.target.value })} data-testid="new-city-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Latitude</Label>
                <Input type="number" step="0.000001" placeholder="62.4622" value={newCity.latitude} onChange={(e) => setNewCity({ ...newCity, latitude: e.target.value })} data-testid="new-city-latitude" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Longitude</Label>
                <Input type="number" step="0.000001" placeholder="-114.4400" value={newCity.longitude} onChange={(e) => setNewCity({ ...newCity, longitude: e.target.value })} data-testid="new-city-longitude" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Radius (km)</Label>
                <Input type="number" step="0.1" min="0.1" placeholder="1" value={newCity.radius_km} onChange={(e) => setNewCity({ ...newCity, radius_km: e.target.value })} data-testid="new-city-radius" />
              </div>
              <div className="md:col-span-6 flex justify-end">
                <Button onClick={addCity} className="bg-summit-primary hover:bg-summit-primary-soft text-white" data-testid="add-city-btn">
                  <Plus className="w-4 h-4 mr-1"/> Add city
                </Button>
              </div>
            </div>
          </div>

          {/* Base-address dialog */}
          <Dialog open={!!addrCity} onOpenChange={(o) => !o && setAddrCity(null)}>
            <DialogContent className="max-w-lg" data-testid="city-address-dialog">
              <DialogHeader><DialogTitle>Base address — {addrCity?.name}</DialogTitle></DialogHeader>
              <p className="text-xs text-gray-500">
                Used to auto-fill addresses in the Stores form generators (e.g. shipper address on the Manitoulin BOL).
              </p>
              <Textarea
                rows={3}
                value={addrText}
                onChange={(e) => setAddrText(e.target.value)}
                placeholder="e.g. 100 Dickins Street, Yellowknife NT Canada"
                data-testid="city-address-input"
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" onClick={() => setAddrCity(null)} data-testid="city-address-cancel">Cancel</Button>
                <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={saveCityAddress} data-testid="city-address-save">Save address</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit-city dialog */}
          <Dialog open={!!editCity} onOpenChange={(o) => !o && setEditCity(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Edit city {editCity?.code}</DialogTitle></DialogHeader>
              {editCity && (
                <div className="grid md:grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Code</Label>
                    <Input value={editCity.code} onChange={(e) => setEditCity({ ...editCity, code: e.target.value.toUpperCase() })} data-testid="edit-city-code" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label>
                    <Input value={editCity.name || ""} onChange={(e) => setEditCity({ ...editCity, name: e.target.value })} data-testid="edit-city-name" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Latitude</Label>
                    <Input type="number" step="0.000001" value={editCity.latitude ?? ""} onChange={(e) => setEditCity({ ...editCity, latitude: e.target.value })} data-testid="edit-city-latitude" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Longitude</Label>
                    <Input type="number" step="0.000001" value={editCity.longitude ?? ""} onChange={(e) => setEditCity({ ...editCity, longitude: e.target.value })} data-testid="edit-city-longitude" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Radius (km) — leave blank for the 1 km default</Label>
                    <Input type="number" step="0.1" min="0.1" value={editCity.radius_km ?? ""} onChange={(e) => setEditCity({ ...editCity, radius_km: e.target.value })} data-testid="edit-city-radius" />
                  </div>
                  <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setEditCity(null)} data-testid="edit-city-cancel">Cancel</Button>
                    <Button onClick={saveEditCity} className="bg-summit-primary hover:bg-summit-primary-soft text-white" data-testid="edit-city-save">Save</Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* BACKUP / RESTORE TAB — System Admin only.
            GET /admin/backup returns a JSON snapshot; POST restore accepts
            the same JSON with mode = merge (upsert by id) or replace. */}
        <TabsContent value="backup" className="mt-6">
          <BackupPanel />
        </TabsContent>

        {/* TOOL DUE-BACK NUDGE TAB — daily 02:00 MST email recipients. */}
        <TabsContent value="toolnudge" className="mt-6">
          <ToolNudgePanel />
        </TabsContent>

        {/* SEARCH ENGINE LOGINS TAB — PartsBase + ILS marketplace credentials. */}
        <TabsContent value="searchcreds" className="mt-6">
          <SearchCredsPanel />
        </TabsContent>

        {/* T/S HELPER MANUALS TAB — learned IPC / AMM manuals (upload + manage). */}
        <TabsContent value="tsmanuals" className="mt-6">
          <TSManualsPanel />
        </TabsContent>

        {/* REVIEW RATINGS TAB */}
        <TabsContent value="ratings" className="mt-6">
          <div className="border border-summit-hairline rounded-lg bg-white">
            <div className="px-5 py-4 border-b border-summit-hairline">
              <h2 className="font-display text-lg font-medium tracking-tight">Performance review ratings</h2>
              <p className="text-xs text-gray-500 mt-1">Categories a manager can pick when posting a Mid-Year or Year-End review.</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-summit-hairline">
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Order</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">ID</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {reviewRatings.length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-6 text-center text-gray-400 text-sm">No ratings yet.</td></tr>
                )}
                {reviewRatings.map((r) => (
                  <tr key={r.id} className="border-b border-summit-hairline last:border-0" data-testid={`rating-row-${r.id}`}>
                    <td className="px-4 py-2 font-medium">{r.name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{r.order}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{r.id}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => removeRating(r.id)}
                        className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"
                        data-testid={`delete-rating-${r.id}`}
                      ><Trash2 className="w-4 h-4"/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-5">
            <h3 className="font-display text-lg font-medium tracking-tight">Add rating</h3>
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label>
                <Input
                  placeholder="e.g. Needs Improvement"
                  value={newRating.name}
                  onChange={(e) => setNewRating({ name: e.target.value })}
                  data-testid="new-rating-name"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={addRating} className="bg-summit-primary hover:bg-summit-primary-soft text-white w-full" data-testid="add-rating-btn">
                  <Plus className="w-4 h-4 mr-1"/> Add
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* TIMESHEET TEMPLATE TAB */}
        <TabsContent value="template" className="mt-6">
          <div className="border border-summit-hairline rounded-lg bg-white p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" /> Timesheet template
                </h2>
                <p className="text-xs text-gray-500 mt-1 max-w-xl">
                  Upload a new .xlsm timesheet template. Aircraft list is auto-parsed from row 16, columns D through AB.
                  New tails are added as active aircraft; missing tails are archived (past sessions remain intact).
                </p>
              </div>
            </div>

            <div className="mt-5 grid md:grid-cols-2 gap-4">
              <div className="border border-summit-hairline rounded-md p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Current template</div>
                {templateSettings?.filename ? (
                  <>
                    <div className="mt-1 font-medium truncate" data-testid="template-filename">{templateSettings.filename}</div>
                    <div className="mt-1 font-mono text-[10px] text-gray-500">
                      Uploaded {templateSettings.uploaded_at ? new Date(templateSettings.uploaded_at).toLocaleString() : "—"}
                      {templateSettings.uploaded_by_name ? ` · ${templateSettings.uploaded_by_name}` : ""}
                    </div>
                    <div className="mt-2 font-mono text-[10px] text-gray-500">
                      {templateSettings.aircraft_count} aircraft
                    </div>
                  </>
                ) : (
                  <div className="mt-1 text-sm text-gray-500" data-testid="template-default">
                    Using packaged default template.
                  </div>
                )}
              </div>

              <div className="border border-summit-hairline rounded-md p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Upload new</div>
                <label className="mt-2 inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsm,.xlsx,application/vnd.ms-excel.sheet.macroEnabled.12"
                    className="hidden"
                    onChange={(e) => uploadTemplate(e.target.files?.[0])}
                    disabled={templateUploading}
                    data-testid="template-upload-input"
                  />
                  <span className="inline-flex items-center h-10 px-4 rounded-md bg-summit-primary text-white text-sm hover:bg-summit-primary-soft" data-testid="template-upload-btn">
                    <Upload className="w-4 h-4 mr-1.5" /> {templateUploading ? "Uploading…" : "Select .xlsm file"}
                  </span>
                </label>
                <p className="mt-3 font-mono text-[10px] text-gray-400">
                  Max 20 MB · Aircraft parsed from row 16, columns D → AB
                </p>
              </div>
            </div>

            {templateLastResult && (
              <div className="mt-5 border border-summit-hairline rounded-md bg-summit-glacier-soft p-4" data-testid="template-upload-result">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Last upload · aircraft changes</div>
                <div className="mt-2 grid md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="font-medium text-emerald-700">Added ({templateLastResult.added.length})</div>
                    <div className="font-mono text-xs text-gray-600 mt-1">
                      {templateLastResult.added.length === 0 ? "—" : templateLastResult.added.join(", ")}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-rose-700">Removed ({templateLastResult.removed.length})</div>
                    <div className="font-mono text-xs text-gray-600 mt-1">
                      {templateLastResult.removed.length === 0 ? "—" : templateLastResult.removed.join(", ")}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Kept ({templateLastResult.kept.length})</div>
                    <div className="font-mono text-xs text-gray-600 mt-1">
                      {templateLastResult.kept.length === 0 ? "—" : templateLastResult.kept.join(", ")}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Current aircraft ({templateAircraftList.length})</div>
              <div className="border border-summit-hairline rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-summit-glacier-soft">
                    <tr className="text-left text-gray-500 border-b border-summit-hairline">
                      <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">#</th>
                      <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Tail</th>
                      <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Fleet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templateAircraftList.map((a, idx) => (
                      <tr key={a.tail} className="border-b border-summit-hairline last:border-0" data-testid={`aircraft-row-${a.tail}`}>
                        <td className="px-4 py-2 font-mono text-xs text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-2 font-mono">{a.tail}</td>
                        <td className="px-4 py-2 text-gray-600">{a.fleet}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* RDB MERGE EXPORT TEMPLATE */}
          <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-5" data-testid="rdb-template-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" /> RDB Merge export template
                </h2>
                <p className="text-xs text-gray-500 mt-1 max-w-xl">
                  Optional second template used as the base file when exporting timesheets. Everything in it is kept
                  intact — formulas, macros, links — and only the time entries are written in, so exported files work
                  with the RDB Merge tool. The main template above keeps all its functions (aircraft list, etc.).
                </p>
              </div>
            </div>

            <div className="mt-5 grid md:grid-cols-2 gap-4">
              <div className="border border-summit-hairline rounded-md p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Current export template</div>
                {rdbTemplate?.filename ? (
                  <>
                    <div className="mt-1 font-medium truncate" data-testid="rdb-template-filename">{rdbTemplate.filename}</div>
                    <div className="mt-1 font-mono text-[10px] text-gray-500">
                      Uploaded {rdbTemplate.uploaded_at ? new Date(rdbTemplate.uploaded_at).toLocaleString() : "—"}
                      {rdbTemplate.uploaded_by_name ? ` · ${rdbTemplate.uploaded_by_name}` : ""}
                    </div>
                    <div className="mt-1 font-mono text-[10px] text-emerald-700">
                      Active — exports use this file, kept intact
                    </div>
                    <button
                      onClick={removeRdbTemplate}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-rose-700 hover:underline"
                      data-testid="rdb-template-remove-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove (back to standard export)
                    </button>
                  </>
                ) : (
                  <div className="mt-1 text-sm text-gray-500" data-testid="rdb-template-none">
                    Not set — exports use the standard template &amp; format.
                  </div>
                )}
              </div>

              <div className="border border-summit-hairline rounded-md p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Upload</div>
                <label className="mt-2 inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsm,.xlsx,application/vnd.ms-excel.sheet.macroEnabled.12"
                    className="hidden"
                    onChange={(e) => { uploadRdbTemplate(e.target.files?.[0]); e.target.value = ""; }}
                    disabled={rdbUploading}
                    data-testid="rdb-template-upload-input"
                  />
                  <span className="inline-flex items-center h-10 px-4 rounded-md bg-summit-primary text-white text-sm hover:bg-summit-primary-soft" data-testid="rdb-template-upload-btn">
                    <Upload className="w-4 h-4 mr-1.5" /> {rdbUploading ? "Uploading…" : "Select .xlsm file"}
                  </span>
                </label>
                <p className="mt-3 font-mono text-[10px] text-gray-400">
                  Max 20 MB · Same Time Sheet layout required (row 16 aircraft, rows 17-30 entries)
                </p>
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="holidays" className="mt-6">
          <div className="border border-summit-hairline rounded-lg bg-white">
            <div className="px-5 py-4 border-b border-summit-hairline">
              <h2 className="font-display text-lg font-medium tracking-tight">Stat holidays</h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-summit-hairline">
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Date</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                  <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Locations</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {holidays.map((h) => (
                  <tr key={h.id} className="border-b border-summit-hairline last:border-0" data-testid={`holiday-row-${h.id}`}>
                    <td className="px-4 py-2 font-mono">{h.date}</td>
                    <td className="px-4 py-2">{h.name}</td>
                    <td className="px-4 py-2"><div className="flex flex-wrap gap-1">{(h.locations || []).map((loc) => <Badge key={loc} variant="secondary" className="font-mono text-[10px]">{loc}</Badge>)}</div></td>
                    <td className="px-4 py-2 text-right"><button onClick={() => removeHoliday(h.id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600" data-testid={`delete-holiday-${h.id}`}><Trash2 className="w-4 h-4"/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-5">
            <h3 className="font-display text-lg font-medium tracking-tight">Add holiday</h3>
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <div><Label className="text-xs uppercase tracking-widest text-gray-500">Date</Label><Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({...newHoliday, date: e.target.value})} data-testid="new-holiday-date" /></div>
              <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label><Input placeholder="Holiday name" value={newHoliday.name} onChange={(e) => setNewHoliday({...newHoliday, name: e.target.value})} data-testid="new-holiday-name" /></div>
            </div>
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Applies to locations</Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button type="button" onClick={() => toggleHolidayLocation("ALL")} className={`px-2.5 py-1 rounded-full text-xs font-mono border ${newHoliday.locations.includes("ALL") ? "bg-summit-primary text-white border-summit-primary" : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"}`} data-testid="loc-toggle-ALL">ALL</button>
                {locations.map((loc) => (
                  <button key={loc.code} type="button" onClick={() => toggleHolidayLocation(loc.code)} className={`px-2.5 py-1 rounded-full text-xs font-mono border ${newHoliday.locations.includes(loc.code) ? "bg-summit-primary text-white border-summit-primary" : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"}`} data-testid={`loc-toggle-${loc.code}`}>{loc.code}</button>
                ))}
              </div>
            </div>
            <div className="mt-4 flex justify-end"><Button onClick={addHoliday} className="bg-summit-primary hover:bg-summit-primary-soft text-white" data-testid="add-holiday-btn"><Plus className="w-4 h-4 mr-1"/> Add</Button></div>
          </div>
        </TabsContent>

        {/* -------- Delegations: admin assigns stand-in approvers -------- */}
        <TabsContent value="delegations" className="mt-6">
          <DelegationsPanel users={users} />
        </TabsContent>

        {/* -------- Repair approvals: per-user page access -------- */}
        <TabsContent value="repairs" className="mt-6">
          <RepairAccessPanel />
          <GseQrPinCard />
        </TabsContent>

        {/* -------- Danger zone: full data reset -------- */}
        <TabsContent value="danger" className="mt-6">
          <DangerZoneReset onDone={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function RepairAccessPanel() {
  const [rows, setRows] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);
  const [query, setQuery] = React.useState("");
  const [page, setPage] = React.useState(1);
  const PAGE_SIZE = 20;

  const load = async () => {
    try {
      const r = await api.get("/admin/users");
      setRows(r.data || []);
    } catch {
      setRows([]);
      toast.error("Failed to load users");
    }
  };
  React.useEffect(() => { load(); }, []);

  const toggle = async (u, patch) => {
    setBusyId(u.id);
    try {
      const r = await api.patch(`/admin/repair-access/${u.id}`, patch);
      setRows((rs) => rs.map((row) => (row.id === u.id ? { ...row, ...r.data } : row)));
      toast.success(`${u.last_name}, ${u.first_initial} — updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update access");
    } finally {
      setBusyId(null);
    }
  };

  const granted = (rows || []).filter((u) => u.repair_approvals_access).length;
  const approvers = (rows || []).filter((u) => u.repair_approver).length;
  const poUsers = (rows || []).filter((u) => u.po_overview_access).length;
  const partsUsers = (rows || []).filter((u) => u.parts_access).length;
  const filtered = (rows || []).filter((u) => {
    if (!query.trim()) return true;
    const hay = `${u.last_name} ${u.first_name} ${u.first_initial} ${u.username || ""} ${u.job_title || ""}`.toLowerCase();
    return hay.includes(query.trim().toLowerCase());
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  return (
    <div className="border border-summit-hairline rounded-lg bg-white p-6" data-testid="repair-access-panel">
      <h2 className="font-display text-xl font-medium tracking-tight">Stores access</h2>
      <p className="text-sm text-gray-500 mt-1">
        <strong>Requestors</strong> see the Repairs page and create requests ({granted}). <strong>Approvers</strong> can be picked as an approver and review straight from their Tasks — no Repairs tab needed ({approvers}). <strong>PO Overview</strong> grants the Stores → PO Overview sheet ({poUsers}). <strong>Parts</strong> grants the Stores → Parts Requests page ({partsUsers}).
      </p>
      <Input
        className="mt-4 max-w-sm"
        placeholder="Search by name, username or job title…"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
        data-testid="repair-access-search"
      />
      <div className="mt-4 divide-y divide-summit-hairline">
        {rows === null ? (
          <p className="py-6 text-sm text-gray-400">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="py-6 text-sm text-gray-400" data-testid="repair-access-no-match">No users match “{query}”.</p>
        ) : (
          pageRows.map((u) => (
            <div key={u.id} className="py-3" data-testid={`repair-access-row-${u.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{u.last_name}, {u.first_name || u.first_initial}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{u.job_title || roleLabel(u.role)}</div>
              </div>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Requestor
                  <Switch
                    checked={!!u.repair_approvals_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { access: v })}
                    data-testid={`repair-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Approver
                  <Switch
                    checked={!!u.repair_approver}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { approver: v })}
                    data-testid={`repair-approver-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  PO Overview
                  <Switch
                    checked={!!u.po_overview_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { po: v })}
                    data-testid={`po-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Parts
                  <Switch
                    checked={!!u.parts_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { parts: v })}
                    data-testid={`parts-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  PR Controls
                  <Switch
                    checked={!!u.pr_controls_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { pr_controls: v })}
                    data-testid={`pr-controls-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Search Engine
                  <Switch
                    checked={!!u.search_engine_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { search_engine: v })}
                    data-testid={`search-engine-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  T/S Helper
                  <Switch
                    checked={!!u.ts_helper_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { ts_helper: v })}
                    data-testid={`ts-helper-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Crit Spares
                  <Switch
                    checked={!!u.critical_spares_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { crit_spares: v })}
                    data-testid={`crit-spares-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Crit Edit
                  <Switch
                    checked={!!u.critical_spares_edit}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { crit_spares_edit: v })}
                    data-testid={`crit-spares-edit-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Spares Mon
                  <Switch
                    checked={!!u.spares_monitoring_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { spares_monitoring: v })}
                    data-testid={`spares-monitoring-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Planning
                  <Switch
                    checked={!!u.planning_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { planning: v })}
                    data-testid={`planning-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Heavy Check
                  <Switch
                    checked={!!u.heavy_check_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { heavy_check: v })}
                    data-testid={`heavy-check-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Handovers
                  <Switch
                    checked={!!u.handover_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { handover: v })}
                    data-testid={`handover-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  Capital
                  <Switch
                    checked={!!u.capital_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { capital: v })}
                    data-testid={`capital-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  GSE
                  <Switch
                    checked={!!u.gse_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { gse: v })}
                    data-testid={`gse-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  GSE Manage
                  <Switch
                    checked={!!u.gse_manage}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { gse_manage: v })}
                    data-testid={`gse-manage-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  M-Shop
                  <Switch
                    checked={!!u.mshop_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { mshop: v })}
                    data-testid={`mshop-access-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  M-Shop Manage
                  <Switch
                    checked={!!u.mshop_manage}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { mshop_manage: v })}
                    data-testid={`mshop-manage-toggle-${u.id}`}
                  />
                </label>
                <label className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white">
                  KPI
                  <Switch
                    checked={!!u.kpi_access}
                    disabled={busyId === u.id}
                    onCheckedChange={(v) => toggle(u, { kpi: v })}
                    data-testid={`kpi-access-toggle-${u.id}`}
                  />
                </label>
              </div>
            </div>
          ))
        )}
      </div>
      {filtered.length > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between border-t border-summit-hairline pt-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="repair-access-page-label">
            Page {safePage} of {totalPages} · {filtered.length} users
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="repair-access-prev">Previous</Button>
            <Button size="sm" variant="outline" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="repair-access-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}


function GseQrPinCard() {
  const [pin, setPin] = React.useState("");
  const [loaded, setLoaded] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    api.get("/gse/qr-pin").then((r) => { setPin(r.data.pin || ""); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const save = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN must be exactly 4 digits"); return; }
    setBusy(true);
    try {
      await api.patch("/gse/qr-pin", { pin });
      toast.success("GSE QR PIN updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update PIN");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-4 max-w-md" data-testid="gse-qr-pin-card">
      <h3 className="font-display font-semibold text-sm">GSE QR code PIN</h3>
      <p className="text-xs text-gray-500 mt-1">Anyone scanning a unit&apos;s QR code must enter this 4-digit code before reporting a problem or changing its status.</p>
      <div className="mt-3 flex items-center gap-2">
        <Input
          className="h-9 w-28 font-mono tracking-[0.3em] text-center"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          disabled={!loaded}
          data-testid="gse-qr-pin-input"
        />
        <Button size="sm" className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={save} disabled={busy || !loaded} data-testid="gse-qr-pin-save">
          Save PIN
        </Button>
      </div>
    </div>
  );
}

function DelegationsPanel({ users }) {
  const [rows, setRows] = React.useState([]);
  const [form, setForm] = React.useState({ delegator_id: "", delegate_id: "", start_date: "", end_date: "", notes: "" });
  const [busy, setBusy] = React.useState(false);
  const [creating, setCreating] = React.useState(false);

  // Only users who can hold direct reports are eligible delegators.
  const delegatorCandidates = React.useMemo(
    () => (users || []).filter((u) => u.can_have_reports),
    [users],
  );

  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/delegations");
      setRows(data);
    } catch {
      toast.error("Failed to load delegations");
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    if (!form.delegator_id || !form.delegate_id || !form.start_date || !form.end_date) {
      toast.error("All four fields are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/delegations", form);
      toast.success("Delegation created");
      setForm({ delegator_id: "", delegate_id: "", start_date: "", end_date: "", notes: "" });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create delegation");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Remove delegation: ${label}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/delegations/${id}`);
      toast.success("Delegation removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="delegations-panel">
      <div className="border border-summit-hairline rounded-lg p-4 bg-white">
        <div className="font-display text-lg font-medium tracking-tight mb-1">Approval delegations</div>
        <p className="text-xs text-gray-500 mb-4 max-w-2xl">
          When a crew chief, supervisor, manager, or senior manager is out of office, assign
          a delegate to act on their behalf. The delegate can view + approve those reports&apos;
          timesheets for the duration of the window. The audit trail records the delegate
          plus the on-behalf-of primary.
        </p>

        {/* Create form */}
        <div className="grid gap-3 md:grid-cols-5 items-end" data-testid="delegation-create-form">
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Delegator (out of office)</Label>
            <select
              className="mt-1 w-full h-9 text-xs border border-summit-hairline rounded-md px-2 bg-white"
              value={form.delegator_id}
              onChange={(e) => setForm((f) => ({ ...f, delegator_id: e.target.value }))}
              data-testid="delegation-delegator-select"
            >
              <option value="">Choose an approver…</option>
              {delegatorCandidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.last_name}, {u.first_initial} · {u.job_title || roleLabel(u.role)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Delegate (stand-in)</Label>
            <select
              className="mt-1 w-full h-9 text-xs border border-summit-hairline rounded-md px-2 bg-white"
              value={form.delegate_id}
              onChange={(e) => setForm((f) => ({ ...f, delegate_id: e.target.value }))}
              data-testid="delegation-delegate-select"
            >
              <option value="">Choose a person…</option>
              {(users || [])
                .filter((u) => u.id !== form.delegator_id)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.last_name}, {u.first_initial} · {u.job_title || roleLabel(u.role)}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Start (MST)</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
              className="mt-1 h-9 text-xs"
              data-testid="delegation-start-input"
            />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">End (MST)</Label>
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              className="mt-1 h-9 text-xs"
              data-testid="delegation-end-input"
            />
          </div>
          <Button
            onClick={submit}
            disabled={creating}
            className="h-9 bg-summit-primary hover:bg-summit-primary-soft text-white"
            data-testid="delegation-create-btn"
          >
            {creating ? "Adding…" : "Add delegation"}
          </Button>
        </div>
        <div className="mt-3">
          <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Notes (optional)</Label>
          <Input
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="e.g. Two-week vacation · Leave 12 Aug → 26 Aug"
            className="mt-1 h-9 text-xs max-w-lg"
            data-testid="delegation-notes-input"
          />
        </div>
      </div>

      {/* Existing delegations table */}
      <div className="border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid="delegations-list">
        <table className="w-full text-sm">
          <thead className="bg-summit-canvas">
            <tr className="text-left border-b border-summit-hairline">
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">Delegator</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">Delegate</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">Window</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">Status</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">Notes</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 font-mono text-[11px] uppercase tracking-widest text-gray-400">No delegations yet.</td></tr>
            )}
            {rows.map((d) => (
              <tr key={d.id} className="border-b border-summit-hairline" data-testid={`delegation-row-${d.id}`}>
                <td className="px-3 py-2">
                  <div>{d.delegator_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{d.delegator_job_title || d.delegator_role}</div>
                </td>
                <td className="px-3 py-2">
                  <div>{d.delegate_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{d.delegate_role}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {d.start_date} → {d.end_date}
                </td>
                <td className="px-3 py-2">
                  {d.is_active && (
                    <span className="inline-block px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800" data-testid={`delegation-status-${d.id}`}>Active</span>
                  )}
                  {d.is_upcoming && (
                    <span className="inline-block px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-sky-100 text-sky-800" data-testid={`delegation-status-${d.id}`}>Upcoming</span>
                  )}
                  {d.is_expired && (
                    <span className="inline-block px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-gray-100 text-gray-500" data-testid={`delegation-status-${d.id}`}>Expired</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate" title={d.notes || ""}>{d.notes || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(d.id, `${d.delegator_name} → ${d.delegate_name}`)}
                    disabled={busy}
                    className="text-summit-ember hover:text-red-700 hover:bg-red-50"
                    data-testid={`delegation-remove-${d.id}`}
                  >Remove</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DangerZoneReset({ onDone }) {
  const [confirmText, setConfirmText] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canProceed = confirmText.trim().toUpperCase() === "RESET";

  const runReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset", { confirm: "RESET" });
      setLastResult(data);
      toast.success(`Reset complete · ${data.total} records erased`);
      setConfirmText("");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="border-2 border-summit-ember/50 rounded-lg bg-summit-ember/5 p-6" data-testid="danger-zone-panel">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-summit-ember shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-xl font-semibold text-summit-ember">Reset all user data</h3>
            <p className="mt-1 text-sm text-summit-primary-soft">
              Wipes every clock-in session, task, review, timesheet approval, plus all Stores,
              Capital, Maintenance and Planning data across every user.
              User accounts, aircraft, holidays, shift schedules, task-type catalog, template file, and
              announcements are preserved. <span className="font-semibold text-summit-ember">This cannot be undone.</span>
            </p>

            <div className="mt-5 grid md:grid-cols-2 gap-4">
              <div className="rounded-md border border-summit-emerald/40 bg-summit-emerald/10 p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-summit-emerald">Kept</div>
                <ul className="mt-2 text-sm text-summit-primary-soft space-y-1">
                  <li>· User accounts + passwords</li>
                  <li>· Aircraft fleet + template</li>
                  <li>· Holidays + shift schedules</li>
                  <li>· Task-type catalog + settings</li>
                  <li>· Announcements</li>
                </ul>
              </div>
              <div className="rounded-md border border-summit-ember/40 bg-summit-ember/10 p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-summit-ember">Erased</div>
                <ul className="mt-2 text-sm text-summit-primary-soft space-y-1">
                  <li>· All clock-in sessions & time entries</li>
                  <li>· All tasks (assignments + status)</li>
                  <li>· All performance reviews + ratings</li>
                  <li>· All timesheet approvals (L1 + L2)</li>
                  <li>· All Stores data (repairs, POs, parts, critical spares)</li>
                  <li>· All Capital requests + attachments</li>
                  <li>· All Maintenance work orders</li>
                  <li>· All Planning jobs + files</li>
                </ul>
              </div>
            </div>

            <div className="mt-6">
              <Label className="text-xs uppercase tracking-widest text-summit-primary-soft">
                Type <span className="font-mono text-summit-ember">RESET</span> to enable the button
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESET"
                className="mt-1.5 max-w-xs font-mono"
                data-testid="danger-reset-confirm-input"
              />
              <Button
                variant="destructive"
                onClick={() => setOpen(true)}
                disabled={!canProceed || busy}
                className="mt-4 bg-summit-ember hover:bg-summit-ember/90 text-white"
                data-testid="danger-reset-open-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-1.5" />
                {busy ? "Erasing…" : "Erase all user data"}
              </Button>
            </div>

            {lastResult && (
              <div className="mt-6 rounded-md border border-summit-hairline bg-white p-4 text-sm" data-testid="danger-reset-result">
                <div className="font-mono text-[10px] uppercase tracking-widest text-summit-primary-soft">Last reset</div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 max-w-md font-mono text-[11px]">
                  {Object.entries(lastResult.deleted).map(([coll, n]) => (
                    <React.Fragment key={coll}>
                      <span className="text-summit-primary-soft">{coll}</span>
                      <span className="text-right tabular-nums text-summit-ink">{n}</span>
                    </React.Fragment>
                  ))}
                  <span className="text-summit-ink font-semibold border-t border-summit-hairline pt-1">Total</span>
                  <span className="text-right tabular-nums text-summit-ink font-semibold border-t border-summit-hairline pt-1">
                    {lastResult.total}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="danger-reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-summit-ember flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> Final confirmation
            </AlertDialogTitle>
            <AlertDialogDescription className="text-summit-primary-soft">
              You are about to erase every clock-in session, task, review, and timesheet approval.
              This action is <span className="font-semibold text-summit-ember">irreversible</span> and
              affects every user in the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="danger-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runReset}
              disabled={busy}
              className="bg-summit-ember hover:bg-summit-ember/90 text-white"
              data-testid="danger-reset-confirm-action"
            >
              Yes, erase all user data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function fmtBytes(b) {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}

/** Backup / Restore panel — System Admin only.
 *  Downloads a JSON snapshot of every non-derived collection.
 *  Restores by uploading a snapshot; two modes: "merge" (upsert) and
 *  "replace" (wipe collection first). Replace requires a confirmation
 *  dialog because it can wipe rows added after the backup was taken.
 */
function BackupPanel() {
  const [summary, setSummary] = React.useState(null);
  const [archive, setArchive] = React.useState({ backups: [], retention_weeks: 8, next_run_mst: null });
  const [loading, setLoading] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [runningNow, setRunningNow] = React.useState(false);
  const [restoreFile, setRestoreFile] = React.useState(null);
  const [restoreMode, setRestoreMode] = React.useState("merge");
  const [restoring, setRestoring] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [restoreReport, setRestoreReport] = React.useState(null);
  const [eraseTarget, setEraseTarget] = React.useState(null); // {coll, count}
  const [eraseText, setEraseText] = React.useState("");
  const [erasing, setErasing] = React.useState(false);

  const eraseCollection = async () => {
    setErasing(true);
    try {
      const { data } = await api.post("/admin/backup/erase", { collection: eraseTarget.coll, confirm: eraseText });
      toast.success(`Erased ${data.deleted} record${data.deleted === 1 ? "" : "s"} from ${data.collection}`);
      setEraseTarget(null);
      setEraseText("");
      await loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erase failed");
    } finally {
      setErasing(false);
    }
  };

  const loadSummary = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/admin/backup/summary"),
        api.get("/admin/backup/archive"),
      ]);
      setSummary(s.data);
      setArchive(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load backup summary");
    } finally {
      setLoading(false);
    }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await api.post("/admin/backup/run-now");
      toast.success(`Backup saved: ${data.backup.filename}`);
      await loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run backup");
    } finally {
      setRunningNow(false);
    }
  };

  const downloadArchived = async (filename) => {
    try {
      const { API_BASE, getToken } = await import("@/lib/api");
      const res = await fetch(`${API_BASE}/admin/backup/archive/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error("Failed to download archived backup");
    }
  };

  React.useEffect(() => { loadSummary(); }, []);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const { API_BASE, getToken } = await import("@/lib/api");
      const res = await fetch(`${API_BASE}/admin/backup`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      // Extract filename from Content-Disposition.
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `summit-air-backup-${new Date().toISOString().slice(0, 10)}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded (${filename})`);
    } catch (e) {
      toast.error("Failed to download backup");
    } finally {
      setDownloading(false);
    }
  };

  const performRestore = async () => {
    if (!restoreFile) { toast.error("Pick a backup file first"); return; }
    setRestoring(true);
    setRestoreReport(null);
    try {
      const text = await restoreFile.text();
      let snap;
      try { snap = JSON.parse(text); }
      catch { throw new Error("Not valid JSON"); }
      const { data } = await api.post("/admin/backup/restore", {
        snapshot: snap, mode: restoreMode,
      });
      setRestoreReport(data.report);
      toast.success(`Restore complete (${restoreMode}). Re-load the app to see changes.`);
      await loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Restore failed");
    } finally {
      setRestoring(false);
      setConfirmOpen(false);
    }
  };

  const startRestore = () => {
    if (!restoreFile) { toast.error("Pick a backup file first"); return; }
    if (restoreMode === "replace") { setConfirmOpen(true); return; }
    performRestore();
  };

  return (
    <div className="space-y-6" data-testid="backup-panel">
      <div className="border border-summit-hairline rounded-lg bg-white">
        <div className="px-5 py-4 border-b border-summit-hairline flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
              <DatabaseBackup className="w-5 h-5 text-summit-primary" /> Backup
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">
              Download a full JSON snapshot of every application collection (users, aircraft, cities, sessions, timesheets, etc.). Keep the file
              somewhere safe — re-upload it under <span className="font-semibold">Restore</span> if the database ever gets corrupted.
            </p>
          </div>
          <Button
            onClick={downloadBackup}
            disabled={downloading}
            className="bg-summit-primary hover:bg-summit-primary-soft text-white"
            data-testid="backup-download-btn"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {downloading ? "Preparing…" : "Download backup"}
          </Button>
        </div>
        {summary && (
          <div className="p-5" data-testid="backup-summary">
            <div className="flex items-baseline justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Current snapshot preview</div>
              <button
                onClick={loadSummary}
                disabled={loading}
                className="font-mono text-[10px] uppercase tracking-widest text-summit-primary hover:underline"
                data-testid="backup-refresh"
              >Refresh</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {(() => {
                const sizes = summary.sizes || {};
                const maxSize = Math.max(1, ...Object.values(sizes));
                return Object.entries(summary.counts || {}).map(([coll, n]) => {
                  const sz = sizes[coll] || 0;
                  const szCls = sz >= 10485760 ? "text-rose-600 font-semibold" : sz >= 1048576 ? "text-amber-600 font-semibold" : "text-gray-400";
                  const barColor = sz >= 10485760 ? "#e11d48" : sz >= 1048576 ? "#d97706" : "#94a3b8";
                  return (
                    <div key={coll} className="group relative border border-summit-hairline rounded-md px-3 py-2 bg-summit-glacier/30" data-testid={`backup-coll-${coll}`}>
                      {coll !== "users" && coll !== "settings" && (
                        <button
                          onClick={() => { setEraseTarget({ coll, count: n }); setEraseText(""); }}
                          className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-rose-600"
                          title={`Erase all ${coll} data`}
                          data-testid={`backup-erase-${coll}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{coll}</div>
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-display text-lg font-semibold tabular-nums">{n}</div>
                        <div className={`font-mono text-[10px] tabular-nums ${szCls}`} data-testid={`backup-size-${coll}`}>{fmtBytes(sz)}</div>
                      </div>
                      <div className="mt-1 h-1 rounded-full bg-gray-200/70 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.max(sz > 0 ? 2 : 0, (sz / maxSize) * 100)}%`, backgroundColor: barColor }} />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="mt-3 text-xs text-gray-500">
              <b>{summary.total_documents}</b> documents across <b>{Object.keys(summary.counts || {}).length}</b> collections
              {summary.total_size_bytes != null && <> · <b>{fmtBytes(summary.total_size_bytes)}</b> total</>} · DB <span className="font-mono">{summary.db_name}</span>
            </div>

            <AlertDialog open={!!eraseTarget} onOpenChange={(o) => { if (!o) { setEraseTarget(null); setEraseText(""); } }}>
              <AlertDialogContent data-testid="backup-erase-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-rose-700">Erase all "{eraseTarget?.coll}" data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes <b>{eraseTarget?.count}</b> record{eraseTarget?.count === 1 ? "" : "s"} from{" "}
                    <span className="font-mono">{eraseTarget?.coll}</span> — the rest of the system is untouched.
                    This cannot be undone (restore from a backup if needed). Type{" "}
                    <span className="font-mono font-bold">{eraseTarget?.coll}</span> to confirm.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <Input
                  value={eraseText}
                  onChange={(e) => setEraseText(e.target.value)}
                  placeholder={eraseTarget?.coll}
                  className="font-mono"
                  data-testid="backup-erase-confirm-input"
                />
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="backup-erase-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => { e.preventDefault(); eraseCollection(); }}
                    disabled={erasing || eraseText !== eraseTarget?.coll}
                    className="bg-rose-600 hover:bg-rose-700"
                    data-testid="backup-erase-confirm-btn"
                  >
                    {erasing ? "Erasing…" : "Erase permanently"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {/* Weekly auto-backup archive — Sundays 3AM MST, retains last 8 weeks. */}
      <div className="border border-summit-hairline rounded-lg bg-white" data-testid="backup-archive">
        <div className="px-5 py-4 border-b border-summit-hairline flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-summit-primary" /> Weekly auto-backups
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">
              A snapshot is automatically saved <b>{archive.next_run_mst || "Sundays at 03:00 MST"}</b>. Server retains the last <b>{archive.retention_weeks}</b> weeks — older files are pruned. Download any archived backup below, or trigger a fresh one right now.
            </p>
          </div>
          <Button
            onClick={runNow}
            disabled={runningNow}
            variant="outline"
            className="border-summit-primary text-summit-primary hover:bg-summit-primary hover:text-white"
            data-testid="backup-run-now-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${runningNow ? "animate-spin" : ""}`} />
            {runningNow ? "Saving…" : "Run backup now"}
          </Button>
        </div>
        <div className="p-5">
          {archive.backups.length === 0 && (
            <div className="text-center py-6 text-sm text-gray-400">
              No archived backups yet. The first weekly backup runs Sunday at 03:00 MST — or click <b>Run backup now</b> to create one immediately.
            </div>
          )}
          {archive.backups.length > 0 && (
            <table className="w-full text-sm" data-testid="backup-archive-table">
              <thead>
                <tr className="text-left text-gray-500 border-b border-summit-hairline">
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Filename</th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Saved (MST)</th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-right">Size</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {archive.backups.map((b) => {
                  const mst = new Date(b.mtime_iso).toLocaleString("en-CA", { timeZone: "America/Edmonton", hour12: false });
                  const sizeKb = (b.size_bytes / 1024).toFixed(1);
                  return (
                    <tr key={b.filename} className="border-b border-summit-hairline last:border-0" data-testid={`backup-row-${b.filename}`}>
                      <td className="px-3 py-2 font-mono text-xs">{b.filename}</td>
                      <td className="px-3 py-2 font-mono tabular-nums text-xs text-gray-600">{mst}</td>
                      <td className="px-3 py-2 font-mono tabular-nums text-xs text-gray-600 text-right">{sizeKb} KB</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => downloadArchived(b.filename)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-summit-hairline text-summit-primary hover:bg-summit-glacier font-mono text-[10px] uppercase tracking-widest"
                          data-testid={`backup-download-archive-${b.filename}`}
                        >
                          <Download className="w-3.5 h-3.5" /> Download
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="border border-summit-hairline rounded-lg bg-white">
        <div className="px-5 py-4 border-b border-summit-hairline">
          <h2 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-summit-ember" /> Restore
          </h2>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Upload a JSON snapshot previously downloaded with <b>Download backup</b>. Pick <b>Merge</b> to safely upsert rows (existing docs updated, new docs inserted, extras left alone), or <b>Replace</b> to wipe each collection first — irreversible.
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Backup file (.json)</Label>
            <input
              type="file"
              accept="application/json,.json"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-mono file:uppercase file:tracking-widest file:bg-summit-glacier file:text-summit-primary hover:file:bg-summit-primary hover:file:text-white file:cursor-pointer"
              data-testid="restore-file-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Restore mode</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setRestoreMode("merge")}
                className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest border transition-colors ${
                  restoreMode === "merge"
                    ? "bg-summit-primary text-white border-summit-primary"
                    : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
                }`}
                data-testid="restore-mode-merge"
              >Merge (safe upsert)</button>
              <button
                type="button"
                onClick={() => setRestoreMode("replace")}
                className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest border transition-colors ${
                  restoreMode === "replace"
                    ? "bg-summit-ember text-white border-summit-ember"
                    : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
                }`}
                data-testid="restore-mode-replace"
              >Replace (wipe first)</button>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={startRestore}
              disabled={!restoreFile || restoring}
              className={`text-white ${
                restoreMode === "replace"
                  ? "bg-summit-ember hover:bg-summit-ember/90"
                  : "bg-summit-primary hover:bg-summit-primary-soft"
              }`}
              data-testid="restore-start-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              {restoring ? "Restoring…" : (restoreMode === "replace" ? "Replace collections" : "Merge restore")}
            </Button>
          </div>
          {restoreReport && (
            <div className="border border-summit-hairline rounded-md p-4 bg-summit-glacier/30" data-testid="restore-report">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Restore report</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-summit-hairline">
                    <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest">Collection</th>
                    <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest text-right">Inserted</th>
                    <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest text-right">Updated</th>
                    <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest text-right">Deleted</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(restoreReport).map(([coll, r]) => (
                    <tr key={coll} className="border-b border-summit-hairline/50">
                      <td className="py-1 pr-3 font-mono">{coll}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{r.inserted}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{r.updated}</td>
                      <td className="py-1 pr-3 text-right tabular-nums text-rose-600">{r.deleted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation dialog for the destructive Replace mode. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-summit-ember" /> Replace all collection contents?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will <span className="font-semibold text-summit-ember">delete every row</span> in the collections
              covered by the snapshot, then insert the backed-up rows. Any changes made after the backup was taken
              will be <span className="font-semibold">lost forever</span>. Consider running a fresh backup first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performRestore}
              disabled={restoring}
              className="bg-summit-ember hover:bg-summit-ember/90 text-white"
              data-testid="restore-confirm-action"
            >
              Yes, replace collections
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}



/** Two-tier grouped tabs list (iter-46 redesign).
 *
 * Instead of a single 12-tab strip, the admin picks a section from the top
 * chip row, and the bottom row shows only the tabs inside that section.
 * Each tab is a plain button that flips the parent `Tabs` value via the
 * `onActivate` callback — no shadcn TabsTrigger required, so the styling
 * stays fully custom.
 *
 * Every child tab keeps its original `data-testid` so existing tests + docs
 * continue to work.
 */
/** Tool due-back nudge — daily 02:00 MST email listing every tool still
 * signed out. Recipients are a free-form email list, admin-editable here. */
function ToolNudgePanel() {
  const [emails, setEmails] = useState([""]);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/tool-status/nudge-settings").then((r) => {
      setEmails(r.data.emails?.length ? r.data.emails : [""]);
      setMeta(r.data);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/tool-status/nudge-settings", {
        emails: emails.map((e) => e.trim()).filter(Boolean),
      });
      setEmails(data.emails.length ? data.emails : [""]);
      toast.success(
        data.emails.length
          ? `Saved — nudge goes to ${data.emails.length} address${data.emails.length === 1 ? "" : "es"}`
          : "Recipient list cleared — nudge disabled"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/tool-status/nudge/run");
      if (data.emails_sent > 0) {
        toast.success(`Sent to ${data.emails_sent} recipient${data.emails_sent === 1 ? "" : "s"} — ${data.tools} tool${data.tools === 1 ? "" : "s"} listed`);
      } else if (data.reason === "no tools signed out") {
        toast.info("No tools signed out — nothing to send");
      } else {
        toast.info("No recipients saved yet — add emails and save first");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-summit-hairline rounded-lg bg-white max-w-2xl" data-testid="tool-nudge-panel">
      <div className="px-5 py-4 border-b border-summit-hairline">
        <h2 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
          <Hammer className="w-4 h-4" /> Tool due-back nudge
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Every day at <span className="font-mono">02:00 MST</span>, the addresses below receive an email
          listing every tool still signed out (with who has it and for how long). Nothing is sent when no
          tools are out or the list is empty.
        </p>
        {meta?.updated_at && (
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-gray-400">
            Last saved {meta.updated_at.slice(0, 10)} by {meta.updated_by_name}
          </p>
        )}
      </div>
      <div className="p-5 space-y-2">
        {emails.map((e, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              type="email"
              value={e}
              onChange={(ev) => setEmails((list) => list.map((x, j) => (j === i ? ev.target.value : x)))}
              placeholder="name@flysummitair.com"
              data-testid={`tool-nudge-email-${i}`}
            />
            <button
              type="button"
              onClick={() => setEmails((list) => (list.length > 1 ? list.filter((_, j) => j !== i) : [""]))}
              className="p-2 rounded-md text-gray-400 hover:text-rose-600 hover:bg-rose-50"
              aria-label="Remove email"
              data-testid={`tool-nudge-email-remove-${i}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setEmails((list) => [...list, ""])}
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-summit-primary hover:text-summit-primary-soft"
          data-testid="tool-nudge-email-add"
        >
          <Plus className="w-3.5 h-3.5" /> Add another email
        </button>
        <div className="pt-3 flex items-center gap-2 border-t border-summit-hairline mt-3">
          <Button
            className="bg-summit-primary hover:bg-summit-primary-soft"
            onClick={save}
            disabled={saving}
            data-testid="tool-nudge-save"
          >
            {saving ? "Saving…" : "Save recipients"}
          </Button>
          <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="tool-nudge-send-now">
            <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Sending…" : "Send now (test)"}
          </Button>
        </div>
      </div>
    </div>
  );
}


/** Admin-editable PartsBase + ILS logins for the Stores Search Engine.
 * Passwords are write-only: leave blank to keep the saved one. */
function TSManualsPanel() {
  const [manuals, setManuals] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const fileRef = useRef(null);

  const load = () => {
    api.get("/fleet/ts-helper/manuals").then((r) => setManuals(r.data.manuals || [])).catch(() => {});
  };
  useEffect(load, []);
  const processing = manuals.some((m) => m.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [processing]);

  const ingest = async () => {
    if (!url.trim()) { toast.error("Paste a link to the manual PDF"); return; }
    setBusy(true);
    try {
      await api.post("/fleet/ts-helper/manuals", { url: url.trim() });
      setUrl("");
      toast.success("Learning manual — this runs in the background");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start ingest");
    } finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove "${m.filename}" and everything learned from it?`)) return;
    try {
      await api.delete(`/fleet/ts-helper/manuals/${m.id}`);
      toast.success("Manual removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Manuals must be PDF files"); return; }
    setUploadName(file.name);
    setUploadPct(0);
    try {
      const { data: s } = await api.post("/fleet/ts-helper/manuals/upload/start", { filename: file.name, size: file.size });
      const chunk = s.chunk_size;
      const total = Math.ceil(file.size / chunk);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * chunk, Math.min((i + 1) * chunk, file.size));
        await api.post(`/fleet/ts-helper/manuals/upload/chunk?upload_id=${s.upload_id}&index=${i}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        setUploadPct(Math.round(((i + 1) / (total + 1)) * 100));
      }
      await api.post("/fleet/ts-helper/manuals/upload/finish", { upload_id: s.upload_id });
      setUploadPct(100);
      toast.success(`${file.name} uploaded — learning in the background`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setTimeout(() => { setUploadPct(null); setUploadName(""); }, 1200);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border border-summit-hairline rounded-lg bg-white max-w-3xl" data-testid="ts-manuals-panel">
      <div className="px-5 py-4 border-b border-summit-hairline">
        <h3 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
          <BookOpenCheck className="w-5 h-5 text-summit-primary" /> T/S Helper manuals
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          IPC / AMM manuals the troubleshooting assistant has learned. Tail applicability is auto-detected from
          the file name (tail reg · RJ85 / RJ100). Paste a direct PDF link to add or update a manual — to replace
          one, add the new file then remove the old.
        </p>
      </div>
      <div className="p-5 space-y-3">
        {manuals.length === 0 && (
          <p className="text-sm text-gray-500" data-testid="ts-no-manuals">No manuals learned yet.</p>
        )}
        {manuals.map((m) => (
          <div key={m.id} className="border border-summit-hairline rounded-md px-3 py-2.5 flex items-start justify-between gap-3" data-testid={`ts-manual-${m.id}`}>
            <div className="flex items-start gap-2.5 min-w-0">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium break-words">{m.filename}</div>
                <div className="font-mono text-[10px] text-gray-500 mt-0.5">
                  {m.kind && <span className={`inline-flex px-1 rounded mr-1.5 ${m.kind === "AMM" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>{m.kind}</span>}
                  {m.status === "ready" && (m.tails?.length ? `${m.page_count} pages · ${m.detect_basis === "all RJ" ? "all RJ tails" : m.tails.join(", ")}` : `${m.page_count} pages · no matching tails in fleet`)}
                  {m.status === "processing" && <span className="text-amber-600 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> learning…</span>}
                  {m.status === "error" && <span className="text-red-600 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {m.error || "failed"}</span>}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(m)} className="text-gray-400 hover:text-red-600 shrink-0 h-7 px-2" data-testid={`ts-manual-delete-${m.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        <div className="pt-3 border-t border-summit-hairline space-y-3">
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="ts-upload-input" />
          {uploadPct !== null ? (
            <div data-testid="ts-upload-progress">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span className="truncate flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading {uploadName}</span>
                <span className="font-mono">{uploadPct}%</span>
              </div>
              <div className="h-1.5 bg-summit-glacier rounded-full overflow-hidden">
                <div className="h-full bg-summit-primary transition-all" style={{ width: `${uploadPct}%` }} />
              </div>
            </div>
          ) : (
            <Button variant="outline" className="w-full h-9" onClick={() => fileRef.current?.click()} data-testid="ts-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload PDF from this device
            </Button>
          )}
          <div className="flex items-center gap-2">
            <div className="flex-1 border-t border-summit-hairline" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">or by link</span>
            <div className="flex-1 border-t border-summit-hairline" />
          </div>
          <div className="flex gap-2">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https:// direct link to IPC or AMM PDF" className="h-9 text-sm" data-testid="ts-ingest-url" />
            <Button onClick={ingest} disabled={busy} className="h-9 shrink-0" data-testid="ts-ingest-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Learn manual"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function SearchCredsPanel() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ partsbase: { username: "", password: "" }, ils: { username: "", password: "" } });
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/stores/search-engine/credentials").then((r) => {
      setInfo(r.data);
      setForm({
        partsbase: { username: r.data.partsbase?.username || "", password: "" },
        ils: { username: r.data.ils?.username || "", password: "" },
      });
    }).catch(() => {});
  };
  useEffect(load, []);

  const save = async (provider) => {
    const f = form[provider];
    if (!f.username.trim()) { toast.error("Username is required"); return; }
    setBusy(`save-${provider}`);
    try {
      await api.put("/stores/search-engine/credentials", {
        provider, username: f.username.trim(), password: f.password || null,
      });
      toast.success(`${provider === "partsbase" ? "PartsBase" : "ILS"} login saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy("");
    }
  };

  const test = async (provider) => {
    setBusy(`test-${provider}`);
    try {
      const { data } = await api.post("/stores/search-engine/credentials/test", { provider });
      if (data.ok) toast.success(`${provider === "partsbase" ? "PartsBase" : "ILS"} login works`);
      else toast.error(data.error || "Login failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setBusy("");
    }
  };

  const CARDS = [
    { key: "partsbase", title: "PartsBase", sub: "app.partsbase.com — detailed listings" },
    { key: "ils", title: "ILS Marketplace", sub: "marketplace.ilsmart.com — availability counts" },
  ];

  return (
    <div className="grid md:grid-cols-2 gap-4 max-w-4xl" data-testid="search-creds-panel">
      {CARDS.map(({ key, title, sub }) => (
        <div key={key} className="border border-summit-hairline rounded-lg bg-white">
          <div className="px-5 py-4 border-b border-summit-hairline">
            <h2 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
              <Globe className="w-4 h-4" /> {title}
            </h2>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">{sub}</p>
            {info?.[key]?.updated_at && (
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-gray-400">
                Updated {info[key].updated_at.slice(0, 10)} by {info[key].updated_by_name}
              </p>
            )}
          </div>
          <div className="p-5 space-y-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Username</Label>
              <Input
                value={form[key].username}
                onChange={(e) => setForm((m) => ({ ...m, [key]: { ...m[key], username: e.target.value } }))}
                className="mt-1"
                data-testid={`secreds-${key}-username`}
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Password</Label>
              <Input
                type="password"
                value={form[key].password}
                onChange={(e) => setForm((m) => ({ ...m, [key]: { ...m[key], password: e.target.value } }))}
                placeholder={info?.[key]?.has_password ? "•••••••• saved — leave blank to keep" : "Enter password"}
                className="mt-1"
                data-testid={`secreds-${key}-password`}
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button
                className="bg-summit-primary hover:bg-summit-primary-soft"
                onClick={() => save(key)}
                disabled={busy === `save-${key}`}
                data-testid={`secreds-${key}-save`}
              >
                {busy === `save-${key}` ? "Saving…" : "Save"}
              </Button>
              <Button
                variant="outline"
                onClick={() => test(key)}
                disabled={busy === `test-${key}`}
                data-testid={`secreds-${key}-test`}
              >
                {busy === `test-${key}` ? "Testing…" : "Test login"}
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


const ADMIN_GROUPS = [
  {
    id: "people",
    label: "People",
    emoji: "👥",
    tabs: [
      { value: "users", label: "Users", testid: "tab-users" },
      { value: "departments", label: "Departments", testid: "tab-departments" },
      { value: "delegations", label: "Delegations", testid: "tab-delegations" },
      { value: "repairs", label: "Stores access", testid: "tab-repairs" },
      { value: "ratings", label: "Review ratings", testid: "tab-ratings" },
    ],
  },
  {
    id: "places",
    label: "Places",
    emoji: "📍",
    tabs: [
      { value: "cities", label: "Cities", testid: "tab-cities" },
    ],
  },
  {
    id: "time",
    label: "Time & Scheduling",
    emoji: "📆",
    tabs: [
      { value: "shifts", label: "Shift schedules", testid: "tab-shifts" },
      { value: "holidays", label: "Stat holidays", testid: "tab-holidays" },
      { value: "tasks", label: "Task types", testid: "tab-tasks" },
    ],
  },
  {
    id: "content",
    label: "Content",
    emoji: "📣",
    tabs: [
      { value: "announcements", label: "Announcements", testid: "tab-announcements" },
      { value: "template", label: "Timesheet template", testid: "tab-template" },
    ],
  },
  {
    id: "system",
    label: "System",
    emoji: "⚙️",
    tabs: [
      { value: "backup", label: "Backup", testid: "tab-backup" },
      { value: "toolnudge", label: "Tool nudge", testid: "tab-toolnudge" },
      { value: "searchcreds", label: "Search logins", testid: "tab-searchcreds" },
      { value: "tsmanuals", label: "T/S manuals", testid: "tab-tsmanuals" },
      { value: "danger", label: "Danger zone", testid: "tab-danger", tone: "danger" },
    ],
  },
];

function _findGroup(activeTab) {
  for (const g of ADMIN_GROUPS) {
    if (g.tabs.some((t) => t.value === activeTab)) return g;
  }
  return ADMIN_GROUPS[0];
}

function AdminGroupedTabsList({ activeTab, onActivate, hideSystem }) {
  const groups = hideSystem ? ADMIN_GROUPS.filter((g) => g.id !== "system") : ADMIN_GROUPS;
  const activeGroup = _findGroup(activeTab);
  const [openGroup, setOpenGroup] = React.useState(activeGroup.id);

  // Keep the visible group in sync when the active tab changes externally.
  React.useEffect(() => { setOpenGroup(activeGroup.id); }, [activeGroup.id]);

  const group = groups.find((g) => g.id === openGroup) || groups[0];

  return (
    <div className="space-y-3" data-testid="admin-grouped-tabs">
      {/* Section chips */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Admin section">
        {groups.map((g) => {
          const isActive = g.id === openGroup;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => setOpenGroup(g.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-xs font-mono uppercase tracking-widest transition-colors ${
                isActive
                  ? "bg-summit-primary text-white border-summit-primary"
                  : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
              }`}
              data-testid={`admin-group-${g.id}`}
              aria-pressed={isActive}
            >
              <span className="text-sm leading-none">{g.emoji}</span>
              <span>{g.label}</span>
              <span className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] px-1 ${
                isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
              }`}>{g.tabs.length}</span>
            </button>
          );
        })}
      </div>
      {/* Sub-tab strip for the currently-open group */}
      <div className="flex flex-wrap gap-1 border-b border-summit-hairline pb-1" role="tablist" aria-label={group.label}>
        {group.tabs.map((t) => {
          const isActive = t.value === activeTab;
          const isDanger = t.tone === "danger";
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => onActivate(t.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? isDanger
                    ? "bg-summit-ember/10 text-summit-ember border-b-2 border-summit-ember -mb-[9px]"
                    : "bg-summit-glacier text-summit-primary border-b-2 border-summit-primary -mb-[9px]"
                  : isDanger
                    ? "text-summit-ember hover:bg-summit-ember/5"
                    : "text-gray-700 hover:bg-summit-glacier hover:text-summit-primary"
              }`}
              data-testid={t.testid}
              role="tab"
              aria-selected={isActive}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

